import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import {
  ClockIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ListBulletIcon,
  BoltIcon,
  DocumentTextIcon,
  MicrophoneIcon,
  CodeBracketIcon,
  PencilSquareIcon,
  AcademicCapIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import ProctoringMonitor from '../../components/proctoring/ProctoringMonitor';
import { reportDiag } from '../../lib/candidateDiag';
import logger from '../../utils/logger';
import { sanitizeReturnUrl } from '../../utils/sanitizeUrl';
import {
  buildAssessmentFlowSearch,
  loadAssessmentTakeDraft,
  saveAssessmentTakeDraft,
  clearAssessmentTakeDraft,
} from '../../lib/publicAssessmentFlow';
import { useScreeningSessionEndBeacon } from '../../hooks/useScreeningSessionEndBeacon';
import { QUESTION_TYPES, normalizeQuestionType } from '../../constants/questionTypes';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { java } from '@codemirror/lang-java';
import { format as prettierFormat } from 'prettier/standalone';
import * as babelParser from 'prettier/plugins/babel';
import * as estreeParser from 'prettier/plugins/estree';
import toast from 'react-hot-toast';
import { CODING_TEMPLATES } from '../assessments/codingAssignmentTemplates';
import LikertQuestionnaire from '../../components/assessment/LikertQuestionnaire';
import { useCandidateLanguage } from '../../i18n/candidate';

const HIDDEN_CHECK_LIMIT_PER_QUESTION = 5;

const CODING_TEMPLATE_BY_ID = CODING_TEMPLATES.reduce((acc, tpl) => {
  const k = String(tpl.id || '').trim().toLowerCase();
  if (k) acc[k] = tpl;
  return acc;
}, {});

const CODING_TEMPLATE_BY_TITLE = CODING_TEMPLATES.reduce((acc, tpl) => {
  const k = String(tpl.title || '').trim().toLowerCase();
  if (k) acc[k] = tpl;
  return acc;
}, {});

function metaFromCatalogTemplate(tpl) {
  if (!tpl) return null;
  return {
    templateId: tpl.id,
    problemStatement: tpl.problemStatement,
    sampleInput: tpl.sampleInput,
    sampleOutput: tpl.sampleOutput,
    evaluationLogic: tpl.evaluationLogic,
    starterByLanguage: tpl.starterByLanguage,
    expectedByLanguage: tpl.expectedByLanguage,
  };
}

/** Shown in coding assessment UI; Java omitted until re-enabled. */
const CODING_ASSESSMENT_UI_LANGUAGES = ['nodejs', 'javascript', 'python'];

function normalizeAllowedCodingLanguages(raw) {
  const ok = new Set(CODING_ASSESSMENT_UI_LANGUAGES);
  const list = Array.isArray(raw) ? raw : [];
  const out = [];
  const seen = new Set();
  for (const entry of list) {
    let l = String(entry || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_.-]+/g, '');
    if (l === 'node') l = 'nodejs';
    if (l === 'js') l = 'javascript';
    if (l === 'py') l = 'python';
    if (l === 'java') continue;
    if (!ok.has(l) || seen.has(l)) continue;
    seen.add(l);
    out.push(l);
  }
  return out.length > 0 ? out.slice() : [...CODING_ASSESSMENT_UI_LANGUAGES];
}

function resolveCodingLanguage(preference, allowedList) {
  const allowed = normalizeAllowedCodingLanguages(allowedList);
  let p = String(preference || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_.-]+/g, '');
  if (p === 'node') p = 'nodejs';
  if (p === 'js') p = 'javascript';
  if (p === 'py') p = 'python';
  if (p === 'java') p = '';
  if (allowed.includes(p)) return p;
  return allowed[0] || 'javascript';
}

function getCodeAnswerString(answerValue) {
  if (answerValue == null) return '';
  if (typeof answerValue === 'string') return answerValue;
  if (typeof answerValue === 'object' && typeof answerValue.code === 'string') return answerValue.code;
  return '';
}

function hashStringToSeed(input = '') {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return hash >>> 0;
}

function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleArrayWithSeed(items, seedString) {
  if (!seedString) {
    return items.slice();
  }
  const random = mulberry32(hashStringToSeed(seedString));
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Returns the authoritative duration (minutes) for a single module.
 * Priority: live populated libraryTestId.estimatedTime > snapshot duration > 0.
 */
function getModuleDuration(mod) {
  return mod?.libraryTestId?.estimatedTime ?? mod?.duration ?? 0;
}

const AssessmentTake = () => {
  const { sharingLink } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const navState = location.state || {};
  const queryEmail = (searchParams.get('email') || '').trim();
  const queryName = (searchParams.get('name') || '').trim();
  const queryScreeningId = (searchParams.get('screeningId') || '').trim();
  const queryReturnUrl = searchParams.get('returnUrl') || '';
  const queryHasInterview = searchParams.get('hasInterview') === 'true';
  const queryInterviewSharingLink = (searchParams.get('interviewSharingLink') || '').trim();

  const [assessment, setAssessment] = useState(() => navState.assessment ?? null);
  const [candidateInfo, setCandidateInfo] = useState(() => {
    if (navState.candidateInfo?.email) return navState.candidateInfo;
    return { email: queryEmail, name: queryName };
  });

  const screeningId = navState.screeningId || queryScreeningId || null;
  const t = useCandidateLanguage({ screeningId, assessmentId: assessment?._id });
  const rawReturnUrl = navState.returnUrl || queryReturnUrl;
  const returnUrl = sanitizeReturnUrl(rawReturnUrl);
  const hasInterview = !!(navState.hasInterview ?? queryHasInterview);
  const interviewSharingLink = (navState.interviewSharingLink || queryInterviewSharingLink || '').trim();

  const [hydrating, setHydrating] = useState(() => !navState.assessment && !!queryEmail);
  const [hydrateError, setHydrateError] = useState(null);
  const [isTimerReady, setIsTimerReady] = useState(false);
  const urlSyncedRef = useRef(false);
  const draftAppliedRef = useRef(false);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [moduleState, setModuleState] = useState('taking'); // 'taking' | 'transition'
  const [transitionCountdown, setTransitionCountdown] = useState(null);
  usePreventBack(!!screeningId, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningSessionEndBeacon(screeningId, candidateInfo?.email || '');
  useScreeningLeaveWarning(!!screeningId);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(() => {
    const a = navState.assessment;
    if (!a) return 0;
    if (a.isMultiModule && !a.isRubricBased && a.modules?.length > 0) {
      return getModuleDuration(a.modules[0]) * 60;
    }
    return a.duration != null ? a.duration * 60 : 0;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [proctoringConfig, setProctoringConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isNavigatorOpen, setIsNavigatorOpen] = useState(false); // Start closed on mobile so question view isn't blocked
  const [activeCodeTab, setActiveCodeTab] = useState('description');
  const [codeTestResult, setCodeTestResult] = useState(null);
  const [isTestingCode, setIsTestingCode] = useState(false);
  // Passage reading recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState({}); // { [questionId]: Blob }
  const [audioUrl, setAudioUrl] = useState({}); // { [questionId]: objectURL }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const [questionProgressById, setQuestionProgressById] = useState({});
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  useBodyScrollLock(showTimeWarning || showReviewScreen || showConfirmSubmit);
  /** Code questions: footer/nav "answered" only after a real Test Code API run for that question (not merely non-empty starter). */
  const [codeTestCompletedByQuestionId, setCodeTestCompletedByQuestionId] = useState({});
  const submitAssessmentFnRef = useRef(null);
  const submitAssessmentInFlightRef = useRef(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  const lastCopyPasteReportAt = useRef(0);
  const COPY_PASTE_REPORT_THROTTLE_MS = 2500;
  const FALLBACK_CODE_TEMPLATES = useMemo(
    () => ({
      'remove duplicates from sorted array': {
        templateId: 'remove-duplicates-sorted-array',
        problemStatement:
          'Given a sorted integer array nums, remove duplicates in-place so each unique element appears once. Return the number of unique elements.',
        sampleInput: '[0,0,1,1,1,2,2,3,3,4]',
        sampleOutput: '5',
        evaluationLogic: [
          'Uses two pointers for in-place update',
          'Returns correct unique element count',
          'Preserves sorted order in first k positions',
        ],
        allowedLanguages: ['nodejs', 'javascript', 'python'],
        starterByLanguage: {
          javascript: 'function removeDuplicates(nums) {\n  // Write your code here\n}\n',
          nodejs:
            'function removeDuplicates(nums) {\n  // Write your code here\n}\nmodule.exports = { removeDuplicates };\n',
          python: 'def remove_duplicates(nums):\n    # Write your code here\n    pass\n',
          java:
            'class Solution {\n  public int removeDuplicates(int[] nums) {\n    // Write your code here\n    return 0;\n  }\n}\n',
        },
      },
      'check even number of digits': {
        templateId: 'even-number-of-digits',
        problemStatement:
          'Given an integer array nums, return how many numbers in the array have an even number of digits.',
        sampleInput: '[12,345,2,6,7896]',
        sampleOutput: '2',
        evaluationLogic: [
          'Counts digits for each number accurately',
          'Handles zero and large values',
          'Returns exact total count',
        ],
        allowedLanguages: ['nodejs', 'javascript', 'python'],
        starterByLanguage: {
          javascript: 'function findNumbers(nums) {\n  // Write your code here\n}\n',
          nodejs:
            'function findNumbers(nums) {\n  // Write your code here\n}\nmodule.exports = { findNumbers };\n',
          python: 'def find_numbers(nums):\n    # Write your code here\n    pass\n',
          java:
            'class Solution {\n  public int findNumbers(int[] nums) {\n    // Write your code here\n    return 0;\n  }\n}\n',
        },
      },
      'count vowels in string': {
        templateId: 'count-vowels-string',
        problemStatement:
          'Given a string s, return the total number of vowels (a, e, i, o, u) present in the string. Ignore case.',
        sampleInput: '"Education"',
        sampleOutput: '5',
        evaluationLogic: [
          'Counts vowels case-insensitively',
          'Ignores consonants and symbols',
          'Returns numeric count',
        ],
      },
      'find string length': {
        templateId: 'string-length',
        problemStatement: 'Given a string s, return the length of s (total number of characters).',
        sampleInput: '"Hello"',
        sampleOutput: '5',
        evaluationLogic: [
          'Returns the correct character count',
          'Handles empty strings',
          'Uses standard string length semantics',
        ],
        allowedLanguages: ['nodejs', 'javascript', 'python'],
        starterByLanguage: {
          javascript: 'function getStringLength(s) {\n  // Write your code here\n}\n',
          nodejs:
            'function getStringLength(s) {\n  // Write your code here\n}\nmodule.exports = { getStringLength };\n',
          python: 'def get_string_length(s):\n    # Write your code here\n    pass\n',
          java:
            'class Solution {\n  public int getStringLength(String s) {\n    // Write your code here\n    return 0;\n  }\n}\n',
        },
      },
      'prime number check': {
        templateId: 'prime-number-check',
        problemStatement: 'Given an integer n, return true if n is prime, otherwise return false.',
        sampleInput: '29',
        sampleOutput: 'true',
        evaluationLogic: [
          'Handles n <= 1 correctly',
          'Checks factors up to sqrt(n)',
          'Returns boolean result',
        ],
      },
      'find maximum element in the array': {
        templateId: 'find-maximum-element-array',
        problemStatement: 'Given an integer array nums, return the maximum element in the array.',
        sampleInput: '[3,7,2,9,4]',
        sampleOutput: '9',
        evaluationLogic: [
          'Iterates through all elements',
          'Handles negative and mixed integers',
          'Returns single max value',
        ],
      },
      'two sum': {
        allowedLanguages: ['nodejs', 'javascript', 'python'],
        starterByLanguage: {
          javascript: 'function twoSum(nums, target) {\n  // Write your code here\n}\n',
          nodejs:
            'function twoSum(nums, target) {\n  // Write your code here\n}\nmodule.exports = { twoSum };\n',
          python: 'def two_sum(nums, target):\n    # Write your code here\n    pass\n',
          java:
            'class Solution {\n  public int[] twoSum(int[] nums, int target) {\n    // Write your code here\n    return new int[]{};\n  }\n}\n',
        },
      },
    }),
    []
  );

  // Track mobile viewport for copy-paste restriction
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 639px)');
    const handleChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const searchParamsKey = searchParams.toString();

  useEffect(() => {
    draftAppliedRef.current = false;
    urlSyncedRef.current = false;
    setIsTimerReady(false);
  }, [sharingLink]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (assessment) {
        setHydrating(false);
        return;
      }
      const email = (searchParams.get('email') || '').trim().toLowerCase();
      if (!sharingLink || !email) {
        setHydrating(false);
        return;
      }
      setHydrating(true);
      setHydrateError(null);
      try {
        const res = await axios.get(`/api/assessments/share/${sharingLink}`, { params: { email } });
        const asm = res.data?.assessment;
        if (!asm) throw new Error('Assessment not found');
        const inv = asm.invitedCandidates?.find((c) => String(c.email || '').toLowerCase() === email);
        if (!inv) {
          if (!cancelled) setHydrateError('You are not invited to this assessment.');
          return;
        }
        if (inv.status === 'completed') {
          const qs = buildAssessmentFlowSearch({
            email,
            name: searchParams.get('name') || '',
            screeningId: queryScreeningId,
            returnUrl: queryReturnUrl,
            hasInterview: queryHasInterview,
            interviewSharingLink: queryInterviewSharingLink,
          });
          if (!cancelled) navigate(`/assessment/${sharingLink}/results${qs}`, { replace: true });
          return;
        }
        if (!cancelled) {
          setAssessment(asm);
          setCandidateInfo((prev) => ({
            email,
            name: (searchParams.get('name') || '').trim() || prev.name || email.split('@')[0],
          }));
        }
      } catch (e) {
        if (!cancelled) {
          setHydrateError(e.response?.data?.message || e.message || 'Failed to load assessment.');
        }
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    assessment,
    sharingLink,
    searchParamsKey,
    navigate,
    queryScreeningId,
    queryReturnUrl,
    queryHasInterview,
    queryInterviewSharingLink,
  ]);

  useEffect(() => {
    if (!assessment?._id || !candidateInfo?.email) return;
    if (searchParams.get('email')) {
      urlSyncedRef.current = true;
      return;
    }
    if (urlSyncedRef.current) return;
    urlSyncedRef.current = true;
    const search = buildAssessmentFlowSearch({
      email: candidateInfo.email,
      name: candidateInfo.name,
      screeningId,
      returnUrl: rawReturnUrl,
      hasInterview,
      interviewSharingLink,
    });
    navigate(`/assessment/${sharingLink}/take${search}`, {
      replace: true,
      state: {
        assessment,
        candidateInfo,
        returnUrl: rawReturnUrl,
        hasInterview,
        screeningId,
        interviewSharingLink: interviewSharingLink || undefined,
      },
    });
  }, [
    assessment,
    candidateInfo?.email,
    candidateInfo?.name,
    sharingLink,
    screeningId,
    rawReturnUrl,
    hasInterview,
    interviewSharingLink,
    navigate,
    searchParamsKey,
  ]);

  useEffect(() => {
    if (!assessment?._id || !candidateInfo?.email || draftAppliedRef.current) return;
    const emailLower = candidateInfo.email.trim().toLowerCase();
    const draft = loadAssessmentTakeDraft(sharingLink, emailLower);
    draftAppliedRef.current = true;

    let initialTime = null;
    if (draft && String(draft.assessmentId) === String(assessment._id)) {
      if (typeof draft.timeLeft === 'number' && draft.timeLeft >= 0) {
        initialTime = draft.timeLeft;
      }
      if (draft.answers && typeof draft.answers === 'object') setAnswers(draft.answers);
      if (typeof draft.currentQuestionIndex === 'number' && draft.currentQuestionIndex >= 0) {
        setCurrentQuestionIndex(draft.currentQuestionIndex);
      }
      if (typeof draft.currentModuleIndex === 'number' && draft.currentModuleIndex >= 0) {
        setCurrentModuleIndex(draft.currentModuleIndex);
      }
      if (draft.codeTestCompletedByQuestionId && typeof draft.codeTestCompletedByQuestionId === 'object') {
        setCodeTestCompletedByQuestionId(draft.codeTestCompletedByQuestionId);
      }
      if (draft.questionProgressById && typeof draft.questionProgressById === 'object') {
        setQuestionProgressById(draft.questionProgressById);
      }
    } else if (assessment.isMultiModule && !assessment.isRubricBased && assessment.modules?.length > 0) {
      const draftModIdx = draft?.currentModuleIndex || 0;
      initialTime = ((assessment.modules[draftModIdx]?.libraryTestId?.estimatedTime ?? assessment.modules[draftModIdx]?.duration) || 0) * 60;
    } else if ((assessment.duration || 0) > 0) {
      initialTime = assessment.duration * 60;
    }

    if (initialTime != null) {
      setTimeLeft(initialTime);
    }
    setIsTimerReady(true);
  }, [assessment, sharingLink, candidateInfo?.email]);

  useEffect(() => {
    if (!assessment?._id || !candidateInfo?.email) return;
    const emailLower = candidateInfo.email.trim().toLowerCase();
    const t = setTimeout(() => {
      saveAssessmentTakeDraft(sharingLink, emailLower, {
        assessmentId: assessment._id,
        timeLeft,
        answers,
        currentQuestionIndex,
        questionProgressById,
        codeTestCompletedByQuestionId,
        currentModuleIndex,
      });
    }, 500);
    return () => clearTimeout(t);
  }, [
    timeLeft,
    answers,
    currentQuestionIndex,
    currentModuleIndex,
    questionProgressById,
    codeTestCompletedByQuestionId,
    assessment,
    sharingLink,
    candidateInfo?.email,
  ]);

  const throttledReportCopyPaste = useCallback(
    (attemptedAction, source, description) => {
      if (!sessionId) return;
      const now = Date.now();
      if (now - lastCopyPasteReportAt.current < COPY_PASTE_REPORT_THROTTLE_MS) return;
      lastCopyPasteReportAt.current = now;
      axios
        .post(`/api/proctoring/session/${sessionId}/event`, {
          type: 'copy_paste',
          severity: 'high',
          description,
          metadata: {
            timestamp: new Date().toISOString(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            url: typeof window !== 'undefined' ? window.location.href : '',
            source,
            attemptedAction,
          },
        })
        .catch((err) => console.error('Failed to report copy-paste attempt to proctoring:', err));
    },
    [sessionId]
  );

  // Restrict paste/copy/cut on mobile and report attempt to proctoring
  const preventCopyPasteOnMobile = useCallback(
    (e) => {
      if (!isMobile) return;
      e.preventDefault();
      const action = e.type === 'paste' ? 'paste' : e.type === 'copy' ? 'copy' : 'cut';
      const label = action.charAt(0).toUpperCase() + action.slice(1);
      throttledReportCopyPaste(
        action,
        'mobile_answer_field',
        `Copy/paste attempt detected on mobile (${label} blocked in assessment answer field)`
      );
    },
    [isMobile, throttledReportCopyPaste]
  );

  // Validate candidate status on component mount
  useEffect(() => {
    if (!assessment || !candidateInfo) return;

    // Check if candidate has already completed the assessment
    const candidate = assessment.invitedCandidates?.find(
      (c) => c.email.toLowerCase() === candidateInfo.email.toLowerCase()
    );

    if (candidate && candidate.status === 'completed') {
      alert(t('You have already completed this assessment. You cannot take it again.'));
      const qs = buildAssessmentFlowSearch({
        email: candidateInfo.email,
        name: candidateInfo.name,
        screeningId,
        returnUrl: rawReturnUrl,
        hasInterview,
        interviewSharingLink,
      });
      navigate(`/assessment/${sharingLink}/results${qs}`, {
        state: {
          assessment,
          candidateInfo,
          returnUrl,
          hasInterview,
          screeningId,
          interviewSharingLink: interviewSharingLink || undefined,
        },
      });
      return;
    }
  }, [
    assessment,
    candidateInfo,
    returnUrl,
    navigate,
    sharingLink,
    screeningId,
    hasInterview,
    rawReturnUrl,
    interviewSharingLink,
    t,
  ]);

  // Flatten all questions from tests, direct questions, and custom questions
  const allQuestions = useMemo(() => {
    if (!assessment) return [];

    let activeContent = assessment.content;
    let activeShuffleEnabled = assessment.shuffleQuestionsEnabled;
    let isModuleFallback = false;

    // ─── Rubric-Based: per-candidate random snapshot (completely independent of multi-module) ───
    if (assessment.isRubricBased && assessment.modules?.length > 0) {
      const emailLower = (candidateInfo?.email || '').trim().toLowerCase();
      const candidate = assessment.invitedCandidates?.find(c => String(c.email || '').toLowerCase() === emailLower);
      let allSnapshotQs = [];
      let snapshotSource = 'none';

      if (candidate?.questionSnapshot && candidate.questionSnapshot.length > 0) {
        allSnapshotQs = candidate.questionSnapshot;
        snapshotSource = 'server_snapshot';
      } else {
        snapshotSource = 'client_fallback';
        // Fallback: generate snapshot client-side from modules when server snapshot is unavailable
        assessment.modules.forEach(mod => {
          const moduleQuestions = mod.questionBank || (mod.libraryTestId ? mod.libraryTestId.questions : null);
          if (moduleQuestions) {
            let modQs = moduleQuestions || [];
            // Respect questionsToPick even when it's 0 — use != null, NOT > 0
            // (> 0 would skip the slice for modules configured with 0 questions,
            //  causing all questions in the module to be shown instead of none)
            if (mod.questionsToPick != null && modQs.length > mod.questionsToPick) {
              modQs = modQs.slice(0, mod.questionsToPick);
            }
            const flattenedQs = modQs.map(q => {
              const qObj = q.question && typeof q.question === 'object' ? q.question : q;
              return { ...qObj, points: q.points || 1, moduleId: mod._id };
            });
            allSnapshotQs.push(...flattenedQs);
          }
        });
      }

      // Diagnostic logging
      console.log(`[RUBRIC_DEBUG] source=${snapshotSource}, totalQuestions=${allSnapshotQs.length}, candidateFound=${!!candidate}, candidateEmail=${emailLower}`);
      const typeBreakdown = {};
      allSnapshotQs.forEach(q => { const t = q.type || 'UNDEFINED'; typeBreakdown[t] = (typeBreakdown[t] || 0) + 1; });
      console.log('[RUBRIC_DEBUG] type breakdown:', typeBreakdown);
      allSnapshotQs.forEach((q, i) => {
        if (!q.type || (q.type === 'multiple-choice' && (!q.options || q.options.length === 0))) {
          console.warn(`[RUBRIC_DEBUG] ⚠️ Q${i + 1} issue: type=${q.type}, options=${Array.isArray(q.options) ? q.options.length : 'missing'}, title="${(q.title || q.prompt || '').substring(0, 60)}"`);
        }
      });

      if (allSnapshotQs.length > 0) {
        const mappedQs = allSnapshotQs.map(q => ({ question: q, points: q.points || 1 }));
        activeContent = { questions: mappedQs };
        activeShuffleEnabled = false; // Backend already shuffled
      } else {
        return [];
      }

    // ─── Multi-Module: sequential module navigation (completely independent of rubric) ───────────
    } else if (assessment.isMultiModule && assessment.modules?.length > 0) {
      const currentModule = assessment.modules[currentModuleIndex];
      if (currentModule) {
        const testData = currentModule.libraryTestId || { ...currentModule, questions: currentModule.questionBank || [] };

        if (!currentModule.libraryTestId && (!testData.questions || testData.questions.length === 0)) {
          const customQsForModule = (assessment.content?.customQuestions || []).filter(
            q => String(q.moduleId) === String(currentModule._id) || String(q.moduleId) === String(currentModule.id)
          );
          if (customQsForModule.length > 0) {
            testData.questions = customQsForModule.map(q => ({ question: q, points: q.points || 1 }));
          }
        }

        if ((!testData.questions || testData.questions.length === 0) && (!testData.questionBank || testData.questionBank.length === 0)) return [];
        activeContent = { tests: [{ test: testData }] };
        activeShuffleEnabled = testData.shuffleQuestionsEnabled || false;
      } else {
        return [];
      }
    }

    const baseQuestions = [];
    const hasCustomQuestions = Array.isArray(activeContent?.customQuestions) &&
      activeContent.customQuestions.length > 0;

    logger.log('=== BUILDING ALL QUESTIONS ===');
    logger.log('Assessment content:', activeContent);

    // Add questions from tests
    if (!hasCustomQuestions && activeContent?.tests) {
      logger.log('Processing tests:', activeContent.tests.length);
      activeContent.tests.forEach((testItem, testIndex) => {
        logger.log(`Test ${testIndex + 1}:`, testItem.test?.title);
        if (testItem.test?.questions) {
          logger.log(`  Questions in test:`, testItem.test.questions.length);
          testItem.test.questions.forEach((questionItem, qIndex) => {
            const question = {
              ...questionItem.question,
              source: 'test',
              testTitle: testItem.test.title,
              points: questionItem.points || 1,
            };
            logger.log(`    Question ${qIndex + 1}:`, {
              id: question._id,
              title: question.title,
              type: question.type,
            });
            baseQuestions.push(question);
          });
        }
      });
    }

    // Add direct questions
    if (!hasCustomQuestions && activeContent?.questions) {
      logger.log('Processing direct questions:', activeContent.questions.length);
      activeContent.questions.forEach((questionItem, qIndex) => {
        const question = {
          ...questionItem.question,
          source: 'direct',
          points: questionItem.points || 1,
        };
        logger.log(`  Direct question ${qIndex + 1}:`, {
          id: question._id,
          title: question.title,
          type: question.type,
        });
        baseQuestions.push(question);
      });
    }

    // Add custom questions
    if (hasCustomQuestions) {
      logger.log('Processing custom questions:', activeContent.customQuestions.length);
      activeContent.customQuestions.forEach((question, qIndex) => {
        const questionWithMeta = {
          ...question,
          source: 'custom',
          points: question.points || 1,
        };
        logger.log(`  Custom question ${qIndex + 1}:`, {
          id: questionWithMeta._id,
          title: questionWithMeta.title,
          type: questionWithMeta.type,
        });
        baseQuestions.push(questionWithMeta);
      });
    }

    const emailKey = (candidateInfo?.email || '').trim().toLowerCase();
    const shouldShuffle = Boolean(activeShuffleEnabled && emailKey);
    const seedString = shouldShuffle
      ? `${assessment._id || assessment.id || ''}|${emailKey}`
      : null;
    const orderedQuestions = seedString
      ? shuffleArrayWithSeed(baseQuestions, seedString)
      : baseQuestions;

    logger.log(
      'Final allQuestions array:',
      orderedQuestions.map((q) => ({ id: q._id, title: q.title, type: q.type })),
      shouldShuffle ? ' (shuffled)' : ''
    );

    return orderedQuestions.map((question, index) => ({
      ...question,
      questionNumber: index + 1,
    }));
    // currentModuleIndex MUST be a dependency — for multi-module assessments the question set is
    // derived from assessment.modules[currentModuleIndex]; without it the memo stays on module 0's
    // questions for every module.
  }, [assessment, candidateInfo?.email, currentModuleIndex]);

  // Define handleSubmitAssessment before it's used in useEffect
  const handleSubmitAssessment = useCallback(async () => {
    if (submitAssessmentInFlightRef.current) return;
    submitAssessmentInFlightRef.current = true;
    try {
      setIsSubmitting(true);

      logger.log('=== SUBMITTING ASSESSMENT ===');
      logger.log('Current answers state:', answers);
      logger.log('Answers object keys:', Object.keys(answers));
      logger.log('Answers object values:', Object.values(answers));

      // End proctoring session in background (do not block submission)
      if (sessionId) {
        axios
          .post(`/api/proctoring/session/${sessionId}/end`, {
            status: 'completed',
            finalScore: Object.keys(answers).length,
          })
          .then(() => logger.log('Proctoring session ended'))
          .catch((err) => console.error('Failed to end proctoring session:', err));
      }

      // Convert audio blobs to base64 for Passage Reading questions
      const finalAnswers = { ...answers };
      for (const [questionId, blob] of Object.entries(audioBlob)) {
        if (blob instanceof Blob) {
          try {
            const arrayBuffer = await blob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64 = btoa(binary);
            finalAnswers[questionId] = { audioData: base64, audioType: 'webm' };
            logger.log(`Converted audio blob for question ${questionId}: ${base64.length} chars base64`);
          } catch (audioErr) {
            console.error(`Failed to convert audio for question ${questionId}:`, audioErr);
          }
        }
      }

      let timeSpentVal = assessment.duration * 60 - timeLeft;
      if (assessment.isMultiModule && !assessment.isRubricBased && assessment.modules?.length > 0) {
        timeSpentVal = (getModuleDuration(assessment.modules[currentModuleIndex]) * 60) - timeLeft;
      }

      const submissionData = {
        candidateEmail: candidateInfo.email,
        candidateName: candidateInfo.name,
        answers: finalAnswers,
        questionProgressById,
        timeSpent: timeSpentVal, // Time spent in seconds
        submittedAt: new Date().toISOString(),
      };
      
      if (assessment.isMultiModule && !assessment.isRubricBased) {
         submissionData.moduleId = assessment.modules[currentModuleIndex].libraryTestId?._id || assessment.modules[currentModuleIndex].libraryTestId || assessment.modules[currentModuleIndex]._id || assessment.modules[currentModuleIndex].id;
         // Attach the active proctoring session so the server can link it to this module.
         if (sessionId) submissionData.proctoringSessionId = sessionId;
      }

      logger.log('Submission data being sent:', submissionData);
      
      let submitResponse;
      if (assessment.isMultiModule && !assessment.isRubricBased) {
        submitResponse = await axios.post(
          `/api/assessments/${assessment._id}/submit-module`,
          submissionData
        );
        
        // Check if it's the last module
        if (currentModuleIndex < assessment.modules.length - 1) {
           // Module complete → end current proctoring session (camera off during transition),
           // reset per-module state, advance the module pointer, show transition screen.
           // The next module's session is started when the candidate clicks "Start".
           if (sessionId) {
             axios
               .post(`/api/proctoring/session/${sessionId}/end`, { status: 'completed' })
               .catch((err) => console.error('Failed to end module proctoring session:', err));
             setSessionId(null); // immediately unmounts ProctoringMonitor → camera off
           }
           setShowConfirmSubmit(false);
           setShowReviewScreen(false);
           setIsSubmitting(false);
           submitAssessmentInFlightRef.current = false;
           setAnswers({});
           setQuestionProgressById({});
           setCurrentQuestionIndex(0);
           setTimeLeft(getModuleDuration(assessment.modules[currentModuleIndex + 1]) * 60);
           setCurrentModuleIndex(prev => prev + 1);
           setModuleState('transition');
           return;
        } else {
           // Last module! End its proctoring session and finalize the entire assessment.
           if (sessionId) {
             setSessionId(null); // unmount ProctoringMonitor → camera off
           }
           submitResponse = await axios.post(
             `/api/assessments/${assessment._id}/submit`,
             {
                candidateEmail: candidateInfo.email,
                candidateName: candidateInfo.name,
                answers: [],
                timeSpent: 0
             }
           );
        }
      } else {
        submitResponse = await axios.post(
          `/api/assessments/${assessment._id}/submit`,
          submissionData
        );
      }

      const resultsQs = buildAssessmentFlowSearch({
        email: candidateInfo.email,
        name: candidateInfo.name,
        screeningId,
        returnUrl: rawReturnUrl,
        hasInterview,
        interviewSharingLink,
      });

      // Check if submission was successful
      if (submitResponse.data.status === 'already_completed') {
        alert(t('You have already completed this assessment. Multiple submissions are not allowed.'));
        clearAssessmentTakeDraft(sharingLink, candidateInfo.email.trim().toLowerCase());
        navigate(`/assessment/${sharingLink}/results${resultsQs}`, {
          state: {
            assessment,
            candidateInfo,
            returnUrl,
            hasInterview,
            screeningId,
            interviewSharingLink: interviewSharingLink || undefined,
          },
        });
        return;
      }

      clearAssessmentTakeDraft(sharingLink, candidateInfo.email.trim().toLowerCase());

      if (submitResponse.data.qualified === false) {
        const thankYouParams = new URLSearchParams();
        if (candidateInfo.email) thankYouParams.set('email', candidateInfo.email.trim());
        if (candidateInfo.name) thankYouParams.set('name', candidateInfo.name.trim());
        thankYouParams.set('rejected', 'true');
        const qs = thankYouParams.toString();
        navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
        return;
      }

      // Navigate to the Assessment completed page (existing flow)
      navigate(`/assessment/${sharingLink}/results${resultsQs}`, {
        state: {
          assessment,
          candidateInfo,
          answers,
          returnUrl,
          hasInterview,
          screeningId,
          interviewSharingLink: interviewSharingLink || undefined,
        },
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      // The worst failure in the journey: the candidate has finished a timed
      // assessment and their answers did not land. Previously this left only a
      // browser alert and console noise — nothing server-side — so a lost
      // submission was indistinguishable from a candidate who never submitted.
      // Reported with answer counts so we can tell "the request was blocked"
      // from "the payload was rejected", and see how much work is at risk.
      reportDiag(
        screeningId,
        'assessment',
        'submit_failed',
        {
          assessmentId: assessment?._id || null,
          isMultiModule: !!assessment?.isMultiModule,
          moduleIndex: currentModuleIndex,
          answeredCount: Object.keys(answers || {}).length,
          payloadKB: Math.round(
            (JSON.stringify({ answers, questionProgressById }) || '').length / 1024
          ),
          httpStatus: error?.response?.status ?? null,
          errorCode: error?.code || null,
          errorMessage: String(error?.message || '').slice(0, 120),
          isNetworkFailure: !error?.response,
          isTimeout: error?.code === 'ECONNABORTED',
          wentOffline: typeof navigator !== 'undefined' ? navigator.onLine === false : null,
          serverMessage: String(error?.response?.data?.message || '').slice(0, 160),
        },
        { email: candidateInfo?.email, name: candidateInfo?.name }
      );

      if (error.response?.data?.status === 'already_completed') {
        alert(t('You have already completed this assessment. Multiple submissions are not allowed.'));
        const resultsQs = buildAssessmentFlowSearch({
          email: candidateInfo.email,
          name: candidateInfo.name,
          screeningId,
          returnUrl: rawReturnUrl,
          hasInterview,
          interviewSharingLink,
        });
        clearAssessmentTakeDraft(sharingLink, candidateInfo.email.trim().toLowerCase());
        navigate(`/assessment/${sharingLink}/results${resultsQs}`, {
          state: {
            assessment,
            candidateInfo,
            returnUrl,
            hasInterview,
            screeningId,
            interviewSharingLink: interviewSharingLink || undefined,
          },
        });
        return;
      } else {
        const errorMessage =
          error.response?.data?.message || error.message || 'Unknown error occurred';
        alert(t('Failed to submit assessment: {error}', { error: t(errorMessage) }));
      }
    } finally {
      submitAssessmentInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    assessment,
    candidateInfo,
    answers,
    audioBlob,
    questionProgressById,
    timeLeft,
    navigate,
    sharingLink,
    sessionId,
    screeningId,
    hasInterview,
    rawReturnUrl,
    interviewSharingLink,
    t,
  ]);

  submitAssessmentFnRef.current = handleSubmitAssessment;

  // Begin the next module from the transition screen: start a fresh proctoring session for
  // this module, then flip to taking state. The session start is awaited so there is no
  // gap between the first violation event and a valid session on the server.
  const handleStartNextModule = useCallback(async () => {
    if (!(assessment?.isMultiModule && !assessment?.isRubricBased)) return;
    const mod = assessment.modules?.[currentModuleIndex];
    setTimeLeft(getModuleDuration(mod) * 60);
    setCurrentQuestionIndex(0);

    // Start the proctoring session for this module before entering taking state.
    if (proctoringConfig && candidateInfo?.email && assessment?._id) {
      try {
        const sessionResponse = await axios.post('/api/proctoring/session/start', {
          assessmentId: assessment._id,
          candidateEmail: candidateInfo.email,
          candidateName: candidateInfo.name,
          moduleIndex: currentModuleIndex,
          moduleId: mod?.libraryTestId?._id || mod?.libraryTestId || mod?._id || mod?.id || null,
          moduleTitle: mod?.title || null,
        });
        setSessionId(sessionResponse.data.sessionId);
        logger.log(`Proctoring session started for module ${currentModuleIndex}:`, sessionResponse.data.sessionId);
      } catch (err) {
        console.error('Failed to start proctoring session for module:', err);
        // Continue without proctoring for this module if it fails — do not block the candidate.
      }
    }

    setModuleState('taking');
  }, [assessment, currentModuleIndex, proctoringConfig, candidateInfo]);

  // Auto-start countdown between modules: 7 → 6 → ... → start.
  // Resets to 7 whenever the transition screen appears; cleared immediately if the candidate
  // clicks "Start now" manually.
  useEffect(() => {
    if (moduleState !== 'transition') {
      setTransitionCountdown(null);
      return;
    }
    setTransitionCountdown(7);
    const interval = setInterval(() => {
      setTransitionCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [moduleState]);

  // Fire handleStartNextModule when the countdown hits 0.
  useEffect(() => {
    if (transitionCountdown === 0) {
      handleStartNextModule();
    }
  }, [transitionCountdown, handleStartNextModule]);

  // Timer effect — never auto-submit without a loaded session (refresh drops React Router state).
  useEffect(() => {
    if (!assessment || !candidateInfo?.email || !isTimerReady) return;
    if (!(assessment.duration > 0)) return;
    // Pause the countdown while the between-modules transition screen is shown — the next
    // module's timer must not start until the candidate clicks "Start".
    if (moduleState !== 'taking') return;
    if (timeLeft <= 0) {
      handleSubmitAssessment();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;

        // Show warning when 5 minutes left
        if (newTime === 300) {
          setShowTimeWarning(true);
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, handleSubmitAssessment, assessment, candidateInfo, isTimerReady, moduleState]);

  // Initialize proctoring — fetch config once, then start the first session.
  // For multi-module assessments a fresh session is started per module (see startModuleProctoring).
  useEffect(() => {
    const initializeProctoring = async () => {
      if (!assessment?._id || !candidateInfo?.email) return;

      logger.log('🔍 AssessmentTake: Initializing proctoring...');

      try {
        const configResponse = await axios.get(`/api/proctoring/public/config/${assessment._id}`);
        const config = configResponse.data.config;
        setProctoringConfig(config);

        // For multi-module: start the session for module 0.
        // For single-module: start a normal session (moduleIndex stays null).
        if (assessment.isMultiModule && !assessment.isRubricBased && assessment.modules?.length > 0) {
          const mod = assessment.modules[0];
          const sessionResponse = await axios.post('/api/proctoring/session/start', {
            assessmentId: assessment._id,
            candidateEmail: candidateInfo.email,
            candidateName: candidateInfo.name,
            moduleIndex: 0,
            moduleId: mod.libraryTestId?._id || mod.libraryTestId || mod._id || mod.id || null,
            moduleTitle: mod.title || null,
          });
          setSessionId(sessionResponse.data.sessionId);
          logger.log('Proctoring session started for module 0:', sessionResponse.data.sessionId);
        } else {
          const sessionResponse = await axios.post('/api/proctoring/session/start', {
            assessmentId: assessment._id,
            candidateEmail: candidateInfo.email,
            candidateName: candidateInfo.name,
          });
          setSessionId(sessionResponse.data.sessionId);
          logger.log('Proctoring session started:', sessionResponse.data.sessionId);
        }
      } catch (error) {
        console.error('Failed to initialize proctoring:', error);
        // Continue without proctoring if it fails — do not block the assessment.
      }
    };

    initializeProctoring();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment?._id, candidateInfo?.email, candidateInfo?.name]);

  // Format time display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId, answer) => {
    logger.log('handleAnswerChange called:', { questionId, answer });
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [questionId]: answer,
      };
      logger.log('Updated answers:', newAnswers);
      return newAnswers;
    });
    setQuestionProgressById((prev) => {
      const ex = prev[questionId] || {};
      return {
        ...prev,
        [questionId]: {
          hiddenChecksUsed: Number(ex.hiddenChecksUsed || 0),
          lastFinalCheckPassed:
            typeof ex.lastFinalCheckPassed === 'boolean' ? ex.lastFinalCheckPassed : null,
          status: 'draft',
        },
      };
    });
  };

  const handleCodeAnswerChange = (questionId, payload) => {
    setAnswers((prev) => {
      const current = prev[questionId];
      const normalizedCurrent =
        current && typeof current === 'object'
          ? current
          : { language: 'javascript', code: typeof current === 'string' ? current : '' };

      const merged = {
        ...normalizedCurrent,
        ...payload,
      };
      if ('code' in merged && typeof merged.code !== 'string') {
        merged.code = merged.code == null ? '' : String(merged.code);
      }

      return {
        ...prev,
        [questionId]: merged,
      };
    });
    setQuestionProgressById((prev) => {
      const ex = prev[questionId] || {};
      return {
        ...prev,
        [questionId]: {
          hiddenChecksUsed: Number(ex.hiddenChecksUsed || 0),
          lastFinalCheckPassed:
            typeof ex.lastFinalCheckPassed === 'boolean' ? ex.lastFinalCheckPassed : null,
          status: 'draft',
        },
      };
    });
  };

  const getLanguageLabel = (lang) => {
    if (lang === 'nodejs') return 'NodeJs';
    if (lang === 'javascript') return 'JavaScript';
    if (lang === 'python') return 'Python';
    if (lang === 'java') return 'Java';
    return String(lang || '').trim() || 'JavaScript';
  };

  const getEditorExtensions = useCallback(
    (lang) => {
      const clipboardLock = EditorView.domEventHandlers({
        paste(ev) {
          ev.preventDefault();
          throttledReportCopyPaste(
            'paste',
            'code_editor',
            'Paste blocked in coding challenge editor'
          );
          return true;
        },
        copy(ev) {
          ev.preventDefault();
          throttledReportCopyPaste('copy', 'code_editor', 'Copy blocked in coding challenge editor');
          return true;
        },
        cut(ev) {
          ev.preventDefault();
          throttledReportCopyPaste('cut', 'code_editor', 'Cut blocked in coding challenge editor');
          return true;
        },
        dragover(ev) {
          ev.preventDefault();
          return true;
        },
        drop(ev) {
          ev.preventDefault();
          throttledReportCopyPaste(
            'drop',
            'code_editor',
            'Drag-and-drop blocked in coding challenge editor'
          );
          return true;
        },
      });
      if (lang === 'python') return [clipboardLock, python()];
      if (lang === 'java') return [clipboardLock, java()];
      return [clipboardLock, javascript()];
    },
    [throttledReportCopyPaste]
  );

  const handleMultipleChoiceAnswer = (questionId, selectedOption) => {
    logger.log('handleMultipleChoiceAnswer called:', { questionId, selectedOption });
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [questionId]: selectedOption,
      };
      logger.log('Updated answers (single choice):', newAnswers);
      return newAnswers;
    });
    setQuestionProgressById((prev) => {
      const ex = prev[questionId] || {};
      return {
        ...prev,
        [questionId]: {
          hiddenChecksUsed: Number(ex.hiddenChecksUsed || 0),
          lastFinalCheckPassed:
            typeof ex.lastFinalCheckPassed === 'boolean' ? ex.lastFinalCheckPassed : null,
          status: 'draft',
        },
      };
    });
  };

  const handleMultipleAnswerChange = (questionId, option, isChecked) => {
    setAnswers((prev) => {
      const currentAnswers = prev[questionId] || [];
      let newAnswers;

      if (isChecked) {
        newAnswers = [...currentAnswers, option];
      } else {
        newAnswers = currentAnswers.filter((ans) => ans !== option);
      }

      return {
        ...prev,
        [questionId]: newAnswers,
      };
    });
    setQuestionProgressById((prev) => {
      const ex = prev[questionId] || {};
      return {
        ...prev,
        [questionId]: {
          hiddenChecksUsed: Number(ex.hiddenChecksUsed || 0),
          lastFinalCheckPassed:
            typeof ex.lastFinalCheckPassed === 'boolean' ? ex.lastFinalCheckPassed : null,
          status: 'draft',
        },
      };
    });
  };

  // ── Passage Reading Recording Handlers ──
  const startRecording = useCallback(async (questionId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob((prev) => ({ ...prev, [questionId]: blob }));
        setAudioUrl((prev) => ({ ...prev, [questionId]: url }));
        // Store a marker in answers so the question is treated as "answered"
        setAnswers((prev) => ({ ...prev, [questionId]: `[audio-recording:${blob.size}bytes]` }));
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recordingTimerRef.current);
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Failed to start recording:', err);
      toast.error(t('Could not access microphone. Please check your browser permissions.'));
    }
  }, [t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
  }, []);

  const deleteRecording = useCallback((questionId) => {
    if (audioUrl[questionId]) {
      URL.revokeObjectURL(audioUrl[questionId]);
    }
    setAudioBlob((prev) => { const next = { ...prev }; delete next[questionId]; return next; });
    setAudioUrl((prev) => { const next = { ...prev }; delete next[questionId]; return next; });
    setAnswers((prev) => { const next = { ...prev }; delete next[questionId]; return next; });
  }, [audioUrl]);

  // Cleanup recording on unmount or question change
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      clearInterval(recordingTimerRef.current);
    };
  }, [currentQuestionIndex]);

  const goToQuestion = (index) => {
    if (index >= 0 && index < allQuestions.length) {
      // Stop recording if user navigates away
      if (isRecording) {
        stopRecording();
      }
      setShowReviewScreen(false);
      setCurrentQuestionIndex(index);
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setShowReviewScreen(false);
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  const currentQuestion = allQuestions[currentQuestionIndex];
  // Personality (IPIP-50) questionnaire — standalone or the current module: rendered as pages
  // of statements with no visible countdown, points or right/wrong review.
  const isPersonalityQuestionSet =
    allQuestions.length > 0 &&
    allQuestions.every((q) => normalizeQuestionType(q.type) === QUESTION_TYPES.LIKERT);

  const getQuestionProgress = useCallback(
    (questionId) => {
      const ex = questionProgressById[questionId] || {};
      return {
        status: ex.status === 'sealed' ? 'sealed' : 'draft',
        hiddenChecksUsed: Number(ex.hiddenChecksUsed || 0),
        lastFinalCheckPassed:
          typeof ex.lastFinalCheckPassed === 'boolean' ? ex.lastFinalCheckPassed : null,
      };
    },
    [questionProgressById]
  );

  const getHiddenChecksRemaining = useCallback(
    (questionId) =>
      Math.max(0, HIDDEN_CHECK_LIMIT_PER_QUESTION - getQuestionProgress(questionId).hiddenChecksUsed),
    [getQuestionProgress]
  );

  const isCodeQuestion = useCallback(
    (question) => {
      if (!question) return false;
      const type = String(question.type || '').trim().toLowerCase();
      if (type.includes('code')) return true;
      if (question.codingMeta && (question.codingMeta?.templateId || question.codingMeta?.starterCode)) {
        return true;
      }
      return false;
    },
    []
  );

  const isPassageReadingQuestion = useCallback((question) => {
    if (!question) return false;
    const rawType = typeof question.type === 'string' ? question.type : '';
    const normalized = rawType.trim().toLowerCase();
    if (!normalized && question.passageMeta?.passageText) return true;
    if (normalized === 'passage-reading') return true;
    if (normalized.replace(/\s+/g, '-') === 'passage-reading') return true;
    if (normalized.includes('passage') && normalized.includes('reading')) return true;
    return Boolean(question.passageMeta?.passageText);
  }, []);

  const isParagraphReadingQuestion = useCallback((question) => {
    if (!question) return false;
    if (question.passageMeta?.isParagraphReading) return true;
    const rawType = typeof question.type === 'string' ? question.type : '';
    const normalized = rawType.trim().toLowerCase();
    return normalized === 'paragraph-reading' || normalized.includes('paragraph');
  }, []);

  const sealCurrentQuestionAndContinue = useCallback(() => {
    if (!currentQuestion?._id) return;
    // Stop any active recording before moving on
    if (isRecording && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
    setQuestionProgressById((prev) => {
      const ex = prev[currentQuestion._id] || {};
      return {
        ...prev,
        [currentQuestion._id]: {
          hiddenChecksUsed: Number(ex.hiddenChecksUsed || 0),
          lastFinalCheckPassed:
            typeof ex.lastFinalCheckPassed === 'boolean' ? ex.lastFinalCheckPassed : null,
          status: 'sealed',
        },
      };
    });
    if (currentQuestionIndex === allQuestions.length - 1) {
      setShowReviewScreen(true);
      return;
    }
    goToNextQuestion();
  }, [currentQuestion, currentQuestionIndex,
    currentModuleIndex, allQuestions.length, isRecording]);

  const getEffectiveCodingMeta = useCallback(
    (question) => {
      if (!question) return null;
      const titleKey = String(question.title || '')
        .trim()
        .toLowerCase();
      const tid = String(question.codingMeta?.templateId || '')
        .trim()
        .toLowerCase();
      const catalogMeta = metaFromCatalogTemplate(
        (tid && CODING_TEMPLATE_BY_ID[tid]) || CODING_TEMPLATE_BY_TITLE[titleKey] || null
      );

      const byTemplateId =
        tid &&
        Object.values(FALLBACK_CODE_TEMPLATES).find(
          (tpl) => String(tpl.templateId || '').trim().toLowerCase() === tid
        );
      const byTitle = FALLBACK_CODE_TEMPLATES[titleKey] || null;
      const legacyFallback = byTemplateId || byTitle;

      const db =
        question.codingMeta && typeof question.codingMeta === 'object' ? question.codingMeta : {};
      if (!Object.keys(db).length) {
        return catalogMeta || legacyFallback || null;
      }

      const merged = {
        ...(catalogMeta || {}),
        ...(legacyFallback || {}),
        ...db,
      };

      const coalesceText = (key) => {
        const fromDb = db[key];
        if (fromDb != null && String(fromDb).trim() !== '') return fromDb;
        if (catalogMeta?.[key] != null && String(catalogMeta[key]).trim() !== '') return catalogMeta[key];
        if (legacyFallback?.[key] != null && String(legacyFallback[key]).trim() !== '') {
          return legacyFallback[key];
        }
        return merged[key];
      };

      const evalDb = db.evaluationLogic;
      const evaluationLogic =
        Array.isArray(evalDb) && evalDb.length > 0
          ? evalDb
          : Array.isArray(catalogMeta?.evaluationLogic) && catalogMeta.evaluationLogic.length > 0
            ? catalogMeta.evaluationLogic
            : Array.isArray(legacyFallback?.evaluationLogic) && legacyFallback.evaluationLogic.length > 0
              ? legacyFallback.evaluationLogic
              : merged.evaluationLogic;

      return {
        ...merged,
        problemStatement: coalesceText('problemStatement'),
        sampleInput: coalesceText('sampleInput'),
        sampleOutput: coalesceText('sampleOutput'),
        evaluationLogic,
      };
    },
    [FALLBACK_CODE_TEMPLATES]
  );
  const currentCodingMeta = getEffectiveCodingMeta(currentQuestion);
  const allowedCodingLanguages = normalizeAllowedCodingLanguages(currentCodingMeta?.allowedLanguages);

  const runCodeCheck = useCallback(async (mode) => {
    if (!currentQuestion?._id) return;
    const checkMode = mode === 'hidden' ? 'hidden' : 'sample';
    if (checkMode === 'hidden' && getHiddenChecksRemaining(currentQuestion._id) <= 0) {
      setCodeTestResult({
        correct: false,
        similarity: 0,
        message: 'Hidden check limit reached for this question.',
        mode: 'hidden',
      });
      setActiveCodeTab('test');
      return;
    }
    const answerValue = answers[currentQuestion._id];
    const meta = getEffectiveCodingMeta(currentQuestion);
    const language = resolveCodingLanguage(
      answerValue && typeof answerValue === 'object' ? answerValue.language : null,
      meta?.allowedLanguages
    );
    const code = getCodeAnswerString(answerValue);
    if (!String(code || '').trim()) {
      setCodeTestResult({ correct: false, similarity: 0, message: 'Please write code first.' });
      setActiveCodeTab('test');
      return;
    }

    setIsTestingCode(true);
    setCodeTestResult(null);
    try {
      const res = await axios.post(`/api/assessments/share/${sharingLink}/test-code`, {
        questionId: currentQuestion._id,
        language,
        code,
        mode: checkMode,
        candidateEmail: candidateInfo?.email || '',
        expectedByLanguage: meta?.expectedByLanguage || {},
      });
      const data = res.data || { correct: false, similarity: 0, message: 'No response', mode: checkMode };
      setCodeTestResult(data);
      if (checkMode === 'hidden') {
        const used = Number(data.hiddenChecksUsed || 0);
        const qid = currentQuestion._id;
        setQuestionProgressById((prev) => {
          const ex = prev[qid] || {};
          return {
            ...prev,
            [qid]: {
              status: ex.status === 'sealed' ? 'sealed' : 'draft',
              hiddenChecksUsed: used,
              lastFinalCheckPassed:
                checkMode === 'hidden' ? Boolean(data.correct) : ex.lastFinalCheckPassed ?? null,
            },
          };
        });
      }
      const qid = currentQuestion._id;
      setCodeTestCompletedByQuestionId((prev) => ({ ...prev, [qid]: true }));
    } catch (err) {
      const serverData = err.response?.data || {};
      if (checkMode === 'hidden' && currentQuestion?._id && typeof serverData.hiddenChecksUsed === 'number') {
        const qid = currentQuestion._id;
        setQuestionProgressById((prev) => {
          const ex = prev[qid] || {};
          return {
            ...prev,
            [qid]: {
              status: ex.status === 'sealed' ? 'sealed' : 'draft',
              hiddenChecksUsed: Number(serverData.hiddenChecksUsed || 0),
              lastFinalCheckPassed:
                checkMode === 'hidden' ? Boolean(serverData.correct) : ex.lastFinalCheckPassed ?? null,
            },
          };
        });
      }
      setCodeTestResult({
        correct: false,
        similarity: 0,
        message: serverData.message || 'Failed to test code.',
        mode: checkMode,
        hiddenChecksRemaining:
          checkMode === 'hidden'
            ? typeof serverData.hiddenChecksRemaining === 'number'
              ? serverData.hiddenChecksRemaining
              : getHiddenChecksRemaining(currentQuestion._id)
            : undefined,
      });
    } finally {
      setIsTestingCode(false);
      setActiveCodeTab('test');
    }
  }, [answers, candidateInfo?.email, currentQuestion, getEffectiveCodingMeta, getHiddenChecksRemaining, sharingLink]);

  const handleFormatCode = useCallback(async () => {
    if (!currentQuestion?._id) return;
    const answerValue = answers[currentQuestion._id];
    const meta = getEffectiveCodingMeta(currentQuestion);
    const language = resolveCodingLanguage(
      answerValue && typeof answerValue === 'object' ? answerValue.language : null,
      meta?.allowedLanguages
    );
    const code = getCodeAnswerString(answerValue);

    if (!String(code || '').trim()) {
      toast.error('Please write code before formatting.');
      return;
    }

    if (language !== 'javascript' && language !== 'nodejs') {
      toast.error('Auto-format is currently available for JavaScript/NodeJs only.');
      return;
    }

    try {
      const formatted = await prettierFormat(code, {
        parser: 'babel',
        plugins: [babelParser, estreeParser],
        semi: true,
        singleQuote: true,
      });
      if (typeof formatted !== 'string') {
        toast.error('Unable to format code.');
        return;
      }
      handleCodeAnswerChange(currentQuestion._id, { language, code: formatted });
    } catch (err) {
      toast.error('Unable to format code.');
    }
  }, [answers, currentQuestion, handleCodeAnswerChange, getEffectiveCodingMeta]);

  useEffect(() => {
    setActiveCodeTab('description');
    setCodeTestResult(null);
    setIsTestingCode(false);
  }, [currentQuestion?._id]);

  useEffect(() => {
    if (!currentQuestion?._id) return;
    if (!isCodeQuestion(currentQuestion)) return;

    const questionId = currentQuestion._id;
    const allowedLanguages = normalizeAllowedCodingLanguages(currentCodingMeta?.allowedLanguages);
    const defaultLanguage = allowedLanguages[0] || 'javascript';
    const starterByLanguage = currentCodingMeta?.starterByLanguage || {};

    setAnswers((prev) => {
      const existing = prev[questionId];
      if (existing && typeof existing === 'object' && String(existing.code || '').trim()) return prev;
      if (typeof existing === 'string' && existing.trim()) {
        return {
          ...prev,
          [questionId]: {
            language: defaultLanguage,
            code: existing,
          },
        };
      }

      return {
        ...prev,
        [questionId]: {
          language: defaultLanguage,
          code: starterByLanguage[defaultLanguage] || '',
        },
      };
    });
  }, [currentQuestion?._id, currentQuestion, currentCodingMeta, isCodeQuestion]);

  useEffect(() => {
    if (!currentQuestion?._id || !isCodeQuestion(currentQuestion)) return;
    const qid = currentQuestion._id;
    const allowed = normalizeAllowedCodingLanguages(currentCodingMeta?.allowedLanguages);
    setAnswers((prev) => {
      const ex = prev[qid];
      if (!ex || typeof ex !== 'object') return prev;
      const lng = ex.language;
      if (!lng || allowed.includes(lng)) return prev;
      return { ...prev, [qid]: { ...ex, language: allowed[0] } };
    });
  }, [currentQuestion?._id, currentQuestion, isCodeQuestion, currentCodingMeta]);

  const currentProgress = currentQuestion?._id
    ? getQuestionProgress(currentQuestion._id)
    : { status: 'draft', hiddenChecksUsed: 0 };
  const currentHiddenChecksRemaining = currentQuestion?._id
    ? getHiddenChecksRemaining(currentQuestion._id)
    : HIDDEN_CHECK_LIMIT_PER_QUESTION;

  const hasAnswered = currentQuestion?._id
    ? currentProgress.status === 'sealed' || Boolean(answers[currentQuestion._id])
    : false;

  const isNavigatorAnswered = (question) => {
    if (!question) return false;
    const p = getQuestionProgress(question._id);
    return p.status === 'sealed';
  };

  const isNavigatorDraft = (question) => {
    if (!question) return false;
    const p = getQuestionProgress(question._id);
    return p.status !== 'sealed' && Boolean(answers[question._id]);
  };

  const progress = isPersonalityQuestionSet
    ? (allQuestions.filter((q) => Number(answers[q._id]) >= 1).length / allQuestions.length) * 100
    : ((currentQuestionIndex + 1) / allQuestions.length) * 100;

  useEffect(() => {
    if (!allQuestions.length) return;
    setQuestionProgressById((prev) => {
      const next = { ...prev };
      allQuestions.forEach((q) => {
        if (!q?._id || next[q._id]) return;
        next[q._id] = { status: 'draft', hiddenChecksUsed: 0, lastFinalCheckPassed: null };
      });
      return next;
    });
  }, [allQuestions]);

  // Debug current question
  useEffect(() => {
    if (currentQuestion) {
      logger.log('Current question:', {
        index: currentQuestionIndex,
    currentModuleIndex,
        id: currentQuestion._id,
        title: currentQuestion.title,
        type: currentQuestion.type,
        hasAnswer: !!answers[currentQuestion._id],
        currentAnswer: answers[currentQuestion._id],
      });
    }
  }, [currentQuestionIndex,
    currentModuleIndex, currentQuestion, answers]);

  const activeCodeLanguage = resolveCodingLanguage(
    currentQuestion &&
      answers[currentQuestion._id] &&
      typeof answers[currentQuestion._id] === 'object'
      ? answers[currentQuestion._id].language
      : null,
    currentCodingMeta?.allowedLanguages
  );

  if (hydrating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600">{t('Loading your assessment…')}</p>
        </div>
      </div>
    );
  }

  if (hydrateError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('Assessment not available')}</h1>
          <p className="text-gray-600 text-sm mb-4">{t(hydrateError)}</p>
          {screeningId && queryEmail ? (
            <a
              href={`/apply/${screeningId}/hub?email=${encodeURIComponent(queryEmail)}${queryName ? `&name=${encodeURIComponent(queryName)}` : ''}`}
              className="inline-flex rounded-lg bg-electric-blue px-4 py-2 text-sm font-semibold text-white hover:bg-electric-blue-600"
            >
              {t('Back to screening')}
            </a>
          ) : (
            <p className="text-xs text-gray-500">{t('Open the assessment from your screening link or invitation.')}</p>
          )}
        </div>
      </div>
    );
  }

  if (!assessment || !candidateInfo?.email) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('Assessment not available')}</h1>
          <p className="text-gray-600 text-sm">
            {t('Please use the link from your screening or invitation. If you refreshed, use the same browser tab link that includes your email, or go back to the screening hub.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title={t('Leave this page?')}
        message={t('Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.')}
      />
      {/* Header – compact on mobile */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-semibold text-gray-900 truncate sm:text-lg">{assessment.title}</h1>
            </div>

              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Progress */}
              <div className="text-xs text-gray-600 sm:text-sm whitespace-nowrap">
                {isPersonalityQuestionSet
                  ? t('{answered} of {total} answered', {
                      answered: allQuestions.filter((q) => Number(answers[q._id]) >= 1).length,
                      total: allQuestions.length,
                    })
                  : t('{current} of {total}', { current: currentQuestionIndex + 1, total: allQuestions.length })}
              </div>

              {/* Save status + timer: hidden for the personality questionnaire (no time pressure;
                  the time limit still runs in the background) */}
              {!isPersonalityQuestionSet && (
              <>
              <div
                className={`hidden sm:inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
                  currentProgress.status === 'sealed'
                    ? 'bg-green-50 text-green-800 border-green-300'
                    : hasAnswered
                      ? 'bg-amber-50 text-amber-800 border-amber-300'
                      : 'bg-gray-50 text-gray-600 border-gray-300'
                }`}
              >
                {currentProgress.status === 'sealed'
                  ? t('Sealed')
                  : hasAnswered
                    ? t('Draft saved')
                    : t('Not saved')}
              </div>

              {/* Timer */}
              <div
                className={`flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl border-2 text-base sm:text-lg font-bold shadow-sm ${
                  timeLeft <= 300
                    ? 'bg-red-50 text-red-900 border-red-400 ring-2 ring-red-200 animate-pulse'
                    : timeLeft <= 900
                      ? 'bg-amber-50 text-amber-900 border-amber-400'
                      : 'bg-indigo-50 text-indigo-900 border-indigo-300'
                }`}
              >
                <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline text-[11px] uppercase tracking-wide opacity-80">
                  {t('Time Left')}
                </span>
                <span className="tabular-nums text-lg sm:text-xl leading-none">{formatTime(timeLeft)}</span>
              </div>
              </>
              )}

            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 h-0.5 sm:h-1">
            <div
              className="bg-indigo-600 h-0.5 sm:h-1 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Proctoring Monitor - background only (no UI or toasts for candidates).
          key=sessionId forces a full remount per module, resetting violation counters. */}
      {proctoringConfig && sessionId && (
        <ProctoringMonitor
          key={sessionId}
          config={proctoringConfig}
          sessionId={sessionId}
          assessmentId={assessment._id}
          candidateEmail={candidateInfo.email}
          showUI={false}
          suppressToasts={true}
        />
      )}

      {/* Main Content – leave room for navigator when open on desktop */}
      <div className="max-w-[96rem] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pr-0 sm:pr-0">
        {moduleState === 'transition' && assessment.isMultiModule && !assessment.isRubricBased ? (
          (() => {
            const totalModules = assessment.modules?.length || 0;
            const completedModule = assessment.modules?.[currentModuleIndex - 1];
            const nextModule = assessment.modules?.[currentModuleIndex];
            const nextQuestionCount = nextModule?.questionBank?.length || nextModule?.libraryTestId?.questions?.length || 0;
            
            const IconMap = {
              MicrophoneIcon,
              CodeBracketIcon,
              PencilSquareIcon,
              ListBulletIcon,
              AcademicCapIcon,
              SparklesIcon,
            };

            const getModuleTypeDetails = (mod) => {
              const test = mod?.libraryTestId || { title: mod?.title, estimatedTime: mod?.duration, questions: mod?.questionBank };
              if (!test) return { type: 'Assessment Test', icon: 'AcademicCapIcon', desc: 'Answer a series of questions designed to assess your knowledge, reasoning, and decision-making skills.', colorClass: 'text-indigo-600 bg-indigo-50 border-indigo-200', iconBgClass: 'bg-indigo-100' };
              
              const questions = test.questions || [];
              const hasPronunciation = questions.some(q => {
                const t = typeof q.question?.type === 'string' ? q.question.type.toLowerCase() : '';
                return t.includes('passage-reading') || (t.includes('passage') && t.includes('reading')) || (q.question?.passageMeta?.passageText && !q.question?.passageMeta?.isParagraphReading);
              });
              const hasParagraph = questions.some(q => {
                const t = typeof q.question?.type === 'string' ? q.question.type.toLowerCase() : '';
                return t.includes('paragraph-reading') || t.includes('paragraph') || q.question?.passageMeta?.isParagraphReading;
              });

              const customDesc = test.description?.trim();

              if (hasPronunciation) {
                return {
                  type: 'Pronunciation Assessment',
                  icon: 'MicrophoneIcon',
                  desc: 'The next section requires you to read the displayed words or passage aloud using your microphone. Speak clearly and naturally.',
                  colorClass: 'text-indigo-700 bg-indigo-50 border-indigo-200',
                  iconBgClass: 'bg-indigo-100'
                };
              }
              if (hasParagraph) {
                return {
                  type: 'Reading Comprehension',
                  icon: 'DocumentTextIcon',
                  desc: 'The next section requires you to read a passage and answer questions based on the information provided.',
                  colorClass: 'text-indigo-700 bg-indigo-50 border-indigo-200',
                  iconBgClass: 'bg-indigo-100'
                };
              }
              
              return {
                type: 'Assessment Test',
                icon: 'AcademicCapIcon',
                desc: 'The next section contains a series of questions. Read each question carefully and select or provide the most appropriate answer.',
                colorClass: 'text-indigo-700 bg-indigo-50 border-indigo-200',
                iconBgClass: 'bg-indigo-100'
              };
            };
            
            const nextDetails = getModuleTypeDetails(nextModule);
            const ModuleIcon = IconMap[nextDetails.icon] || AcademicCapIcon;

            return (
              <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 sm:p-10 max-w-2xl mx-auto text-center border border-gray-100 relative overflow-hidden">
                {/* Decorative background element */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-indigo-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
                
                <div className="relative z-10">
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-50 border-4 border-white shadow-sm ring-1 ring-green-100">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 sm:text-2xl tracking-tight">
                    {completedModule?.title ? t('{title} — Complete', { title: completedModule.title }) : t('Module Complete')}
                  </h2>
                  <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                    {t('Your answers for this module have been saved successfully. You cannot return to previous modules.')}
                  </p>

                  <div className={`mt-8 rounded-2xl border bg-white p-1 text-left shadow-sm ${nextDetails.colorClass.split(' ')[2]}`}>
                    <div className={`rounded-xl p-5 sm:p-6 ${nextDetails.colorClass.split(' ')[1]} h-full`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${nextDetails.iconBgClass}`}>
                          <ModuleIcon className={`h-5 w-5 ${nextDetails.colorClass.split(' ')[0]}`} />
                        </div>
                        <div>
                          <p className={`text-[11px] font-bold uppercase tracking-wider ${nextDetails.colorClass.split(' ')[0]} opacity-80`}>{t('Next Module')}</p>
                          <h3 className={`text-base sm:text-lg font-bold mt-0.5 ${nextDetails.colorClass.split(' ')[0]} mix-blend-multiply`}>
                            {nextModule?.title || t('Module {number}', { number: currentModuleIndex + 1 })}
                          </h3>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700 leading-relaxed font-medium mb-5 bg-white/60 p-4 rounded-xl border border-white/40 shadow-sm backdrop-blur-sm">
                        <span className="font-bold block mb-1">{t(nextDetails.type)}</span>
                        {t(nextDetails.desc)}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <div className="inline-flex items-center gap-1.5 bg-white/80 px-3 py-1.5 rounded-lg border border-white/50 font-semibold text-gray-800 shadow-sm">
                          <ClockIcon className="h-4 w-4 text-gray-500" />
                          {t('{minutes} minutes', { minutes: getModuleDuration(nextModule) })}
                        </div>
                        <div className="inline-flex items-center gap-1.5 bg-white/80 px-3 py-1.5 rounded-lg border border-white/50 font-semibold text-gray-800 shadow-sm">
                          <ListBulletIcon className="h-4 w-4 text-gray-500" />
                          {t(nextQuestionCount === 1 ? '{count} question' : '{count} questions', { count: nextQuestionCount })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="mt-6 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('Module {current} of {total} • Starting automatically in {seconds}s', {
                      current: currentModuleIndex + 1,
                      total: totalModules,
                      seconds: transitionCountdown ?? 7,
                    })}
                  </p>

                  <div className="mt-4 flex flex-col items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50 border-2 border-indigo-100 shadow-inner">
                      <span className="text-3xl font-black text-indigo-600 tabular-nums tracking-tighter">
                        {transitionCountdown ?? 7}
                      </span>
                    </div>

                    <button
                      onClick={() => { setTransitionCountdown(null); handleStartNextModule(); }}
                      className="group inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 hover:shadow-indigo-600/40 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 ease-out active:scale-[0.98]"
                    >
                      {t('Start Module Now')}
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })()
        ) : isPersonalityQuestionSet ? (
          <LikertQuestionnaire
            questions={allQuestions}
            answers={answers}
            onAnswer={handleAnswerChange}
            currentIndex={currentQuestionIndex}
            onGoToIndex={goToQuestion}
            onFinish={() => setShowConfirmSubmit(true)}
            isSubmitting={isSubmitting}
            finishLabel={
              assessment.isMultiModule && !assessment.isRubricBased && currentModuleIndex < (assessment.modules?.length || 1) - 1
                ? t('Finish questionnaire')
                : t('Submit')
            }
          />
        ) : currentQuestion ? (
          <div className="bg-white rounded-xl shadow p-4 sm:p-6">
            {/* Question Header – compact on mobile */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="text-base font-medium text-gray-900 sm:text-lg">
                  {t('Question {number}', { number: currentQuestion.questionNumber })}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded">
                  {t(currentQuestion.points !== 1 ? '{count} points' : '{count} point', { count: currentQuestion.points })}
                </span>
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold sm:hidden ${
                    currentProgress.status === 'sealed'
                      ? 'bg-green-50 text-green-800 border-green-300'
                      : hasAnswered
                        ? 'bg-amber-50 text-amber-800 border-amber-300'
                        : 'bg-gray-50 text-gray-600 border-gray-300'
                  }`}
                >
                  {currentProgress.status === 'sealed'
                    ? t('Sealed')
                    : hasAnswered
                      ? t('Draft saved')
                      : t('Not saved')}
                </span>
              </div>

              {currentQuestion.source === 'test' && (
                <span className="text-xs text-gray-500 sm:text-sm">{t('From: {source}', { source: currentQuestion.testTitle })}</span>
              )}
            </div>

            {/* Question Content */}
            <div className="mb-4 sm:mb-6">
              {isParagraphReadingQuestion(currentQuestion) && (
                <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-5 sm:p-6 mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <DocumentTextIcon className="w-5 h-5 text-emerald-600" />
                    <h4 className="text-sm font-semibold text-emerald-900">
                      {currentQuestion.passageMeta?.paragraphTitle || t('Read the following paragraph:')}
                    </h4>
                  </div>
                  <p className="text-base leading-7 text-gray-800 whitespace-pre-wrap">
                    {currentQuestion.passageMeta?.paragraphText || currentQuestion.content}
                  </p>
                </div>
              )}
              {(normalizeQuestionType(currentQuestion.type) === QUESTION_TYPES.FILL_IN_BLANKS) ? (
                <div className="text-base font-medium text-gray-900 mb-6 sm:text-lg leading-relaxed">
                  {(() => {
                    const text = String(currentQuestion.content || currentQuestion.title || '').replace(/_{3,}/g, '________');
                    const parts = text.split('________');
                    if (parts.length === 1) return text;
                    return (
                      <>
                        {parts.map((part, i) => (
                          <React.Fragment key={i}>
                            {part}
                            {i < parts.length - 1 && (
                                <input
                                  type="text"
                                  data-testid="assessment-text-input"
                                  value={answers[currentQuestion._id] || ''}
                                  onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                                  onPaste={preventCopyPasteOnMobile}
                                  className="inline-block w-40 px-2 py-1 mx-2 text-center text-sm border-b-2 border-gray-400 bg-gray-50 focus:outline-none focus:border-indigo-600 focus:bg-white text-indigo-700 font-semibold transition-colors"
                                  placeholder="..."
                                />
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <h3 className="text-base font-medium text-gray-900 mb-2 sm:mb-3 sm:text-lg">
                    {currentQuestion.title}
                  </h3>
                  {/* Avoid duplicate rendering when content repeats or extends the title */}
                  {(() => {
                    const titleText = String(currentQuestion.title || '').replace(/\s+/g, ' ').trim();
                    const contentText = String(currentQuestion.content || '').replace(/\s+/g, ' ').trim();
                    const titleNorm = titleText.toLowerCase();
                    const contentNorm = contentText.toLowerCase();
                    const isRedundant =
                      !contentNorm ||
                      contentNorm === titleNorm ||
                      contentNorm.startsWith(titleNorm) ||
                      titleNorm.startsWith(contentNorm);
                    return contentText && !isRedundant ? (
                      <p className="text-gray-700 mb-4">{currentQuestion.content}</p>
                    ) : null;
                  })()}
                </>
              )}
            </div>

            {/* Question Type and Options */}
            <div className="space-y-4">
              {/* Multiple Choice Questions - Determine if single or multiple based on correct answer */}
              {(currentQuestion.type === 'multiple-choice' ||
                currentQuestion.type === 'Multiple Choice (Single)' ||
                currentQuestion.type === 'Multiple Choice (Multiple)' ||
                currentQuestion.type === 'single-choice' ||
                currentQuestion.type === 'single-choice' ||
                (currentQuestion.type && currentQuestion.type.includes('Multiple Choice'))) &&
                currentQuestion.options && currentQuestion.options.length > 0 && (
                  <div>
                    {/* Determine if this should be multiple answers based on:
                      // 1. Explicit type declaration
                      // 2. Multiple correct answers in the options
                      // 3. Correct answer format (contains multiple letters like "a,c" or "a, c")
                  */}
                    {(() => {
                      // Check if question type explicitly indicates multiple answers
                      const explicitMultipleType =
                        currentQuestion.type === 'Multiple Choice (Multiple)' ||
                        currentQuestion.type === 'multiple-choice-multiple' ||
                        currentQuestion.type === 'Multiple Choice (Multiple Answers)';

                      // Check if question type explicitly indicates single answer
                      const explicitSingleType =
                        currentQuestion.type === 'single-choice' ||
                        currentQuestion.type === 'Multiple Choice (Single)' ||
                        currentQuestion.type === 'Multiple Choice (Single Answer)';
                      // Note: 'multiple-choice' is NOT treated as explicit single - it can have multiple correct answers

                      // Count correct answers in options
                      const correctOptionsCount = currentQuestion.options
                        ? currentQuestion.options.filter((opt) => opt && opt.isCorrect).length
                        : 0;

                      // Check if correctAnswer contains multiple letters (comma-separated)
                      const correctAnswerHasMultiple =
                        currentQuestion.correctAnswer &&
                        currentQuestion.correctAnswer.includes(',');

                      // Determine if this should allow multiple answers
                      // For generic 'multiple-choice' type, check actual correct answer count
                      const isMultipleAnswer =
                        explicitMultipleType || // Explicitly marked as multiple
                        (!explicitSingleType && // Not explicitly single
                          (correctOptionsCount > 1 || correctAnswerHasMultiple)); // Has multiple correct answers

                      // Debug logging
                      logger.log('Question type detection:', {
                        type: currentQuestion.type,
                        correctAnswer: currentQuestion.correctAnswer,
                        correctOptionsCount: currentQuestion.options
                          ? currentQuestion.options.filter((opt) => opt.isCorrect).length
                          : 0,
                        isMultipleAnswer,
                      });

                      if (isMultipleAnswer) {
                        return (
                          <>
                            <p className="text-sm font-medium text-gray-700 mb-3">
                              {t('Select all correct answers:')}
                            </p>
                            <div className="space-y-2">
                              {currentQuestion.options.map((option, index) => (
                                <label
                                  key={index}
                                  className="flex items-center space-x-3 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    data-testid="assessment-option"
                                    checked={(answers[currentQuestion._id] || []).includes(
                                      option.text
                                    )}
                                    onChange={(e) =>
                                      handleMultipleAnswerChange(
                                        currentQuestion._id,
                                        option.text,
                                        e.target.checked
                                      )
                                    }
                                    className="text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <span className="text-gray-700">
                                    {String.fromCharCode(65 + index)}. {option.text}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <p className="text-sm font-medium text-gray-700 mb-3">
                              {t('Select the correct answer:')}
                            </p>
                            <div className="space-y-2">
                              {currentQuestion.options.map((option, index) => {
                                const optionLetter = String.fromCharCode(97 + index); // a, b, c, d
                                return (
                                  <label
                                    key={index}
                                    className="flex items-center space-x-3 cursor-pointer"
                                  >
                                    <input
                                      type="radio"
                                      data-testid="assessment-option"
                                      name={`question-${currentQuestion._id}`}
                                      value={optionLetter}
                                      checked={answers[currentQuestion._id] === optionLetter}
                                      onChange={() =>
                                        handleMultipleChoiceAnswer(
                                          currentQuestion._id,
                                          optionLetter
                                        )
                                      }
                                      className="text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-gray-700">
                                      {String.fromCharCode(65 + index)}. {option.text}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </>
                        );
                      }
                    })()}
                  </div>
                )}

              {/* Safety fallback: MCQ type but options missing/empty — show text input so candidate isn't stuck */}
              {(currentQuestion.type === 'multiple-choice' ||
                currentQuestion.type === 'Multiple Choice (Single)' ||
                currentQuestion.type === 'Multiple Choice (Multiple)' ||
                currentQuestion.type === 'single-choice' ||
                (currentQuestion.type && currentQuestion.type.includes('Multiple Choice'))) &&
                (!currentQuestion.options || currentQuestion.options.length === 0) && (
                  <div>
                    {(() => { console.warn('[AssessmentTake] MCQ question has no options! Falling back to text input. type:', currentQuestion.type, 'qId:', currentQuestion._id, 'title:', (currentQuestion.title || '').substring(0, 60)); return null; })()}
                    <p className="text-sm font-medium text-amber-700 mb-3">{t('Your answer:')}</p>
                    <textarea
                      data-testid="assessment-text-input"
                      value={answers[currentQuestion._id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                      onPaste={preventCopyPasteOnMobile}
                      onCopy={preventCopyPasteOnMobile}
                      onCut={preventCopyPasteOnMobile}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('Type your answer here...')}
                    />
                  </div>
                )}

              {/* True/False Questions */}
              {(currentQuestion.type === 'true-false' || currentQuestion.type === 'True/False') && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    {t('Select the correct answer:')}
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        data-testid="assessment-option"
                        name={`question-${currentQuestion._id}`}
                        value="a"
                        checked={answers[currentQuestion._id] === 'a'}
                        onChange={() => handleMultipleChoiceAnswer(currentQuestion._id, 'a')}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-gray-700">{t('True')}</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        data-testid="assessment-option"
                        name={`question-${currentQuestion._id}`}
                        value="b"
                        checked={answers[currentQuestion._id] === 'b'}
                        onChange={() => handleMultipleChoiceAnswer(currentQuestion._id, 'b')}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-gray-600">{t('False')}</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Passage Reading Questions */}
              {isPassageReadingQuestion(currentQuestion) && (
                <div className="space-y-5">
                  {/* Passage Display */}
                  <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/50 p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h4 className="text-sm font-semibold text-indigo-900">{t('Read the following passage aloud:')}</h4>
                    </div>
                    <p className="text-base leading-7 text-gray-800 whitespace-pre-wrap font-medium">
                      {currentQuestion.passageMeta?.passageText || currentQuestion.content || t('No passage available.')}
                    </p>
                  </div>

                  {/* Recording Controls */}
                  <div className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-14 0m14 0a7 7 0 00-14 0m14 0v1a7 7 0 01-14 0v-1m7 8v3m-4 0h8" />
                      </svg>
                      <h4 className="text-sm font-semibold text-gray-900">{t('Audio Recording')}</h4>
                    </div>

                    {!audioUrl[currentQuestion._id] ? (
                      // No recording yet
                      <div className="text-center py-4">
                        {isRecording ? (
                          // Currently recording
                          <div className="space-y-4">
                            <div className="flex items-center justify-center gap-3">
                              <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
                              <span className="text-lg font-bold text-red-600 tabular-nums">
                                {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:{(recordingTime % 60).toString().padStart(2, '0')}
                              </span>
                              <span className="text-sm text-gray-500">{t('Recording...')}</span>
                            </div>
                            <p className="text-xs text-gray-500">{t('Read the passage clearly into your microphone')}</p>
                            <button
                              type="button"
                              onClick={stopRecording}
                              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-sm transition-colors"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <rect x="6" y="6" width="12" height="12" rx="2" />
                              </svg>
                              {t('Stop Recording')}
                            </button>
                          </div>
                        ) : (
                          // Not started
                          <div className="space-y-4">
                            <div className="w-16 h-16 mx-auto rounded-full bg-indigo-100 flex items-center justify-center">
                              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-14 0m14 0a7 7 0 00-14 0m14 0v1a7 7 0 01-14 0v-1m7 8v3m-4 0h8" />
                              </svg>
                            </div>
                            <p className="text-sm text-gray-600">
                              {t('Click the button below to start recording. Read the passage above clearly and at a natural pace.')}
                            </p>
                            <button
                              type="button"
                              onClick={() => startRecording(currentQuestion._id)}
                              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-sm transition-colors"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="6" />
                              </svg>
                              {t('Start Recording')}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Recording complete — show playback
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                          <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-green-800">{t('Recording saved successfully')}</span>
                        </div>
                        <audio controls className="w-full" src={audioUrl[currentQuestion._id]} />
                        <button
                          type="button"
                          onClick={() => deleteRecording(currentQuestion._id)}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t('Re-record')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Text and Essay Questions */}
              {(currentQuestion.type === 'text' ||
                currentQuestion.type === 'essay' ||
                currentQuestion.type === 'short-answer' ||
                currentQuestion.type === 'Short Answer / Fill in the Blank' ||
                currentQuestion.type === 'Write the basic accounting equation' ||
                (currentQuestion.type && currentQuestion.type.toLowerCase().includes('write')) ||
                (currentQuestion.type && currentQuestion.type.toLowerCase().includes('essay')) ||
                (currentQuestion.type && currentQuestion.type.toLowerCase().includes('text'))) &&
                !isCodeQuestion(currentQuestion) &&
                !isPassageReadingQuestion(currentQuestion) && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">{t('Your answer:')}</p>
                  <textarea
                    data-testid="assessment-text-input"
                    value={answers[currentQuestion._id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                    onPaste={preventCopyPasteOnMobile}
                    onCopy={preventCopyPasteOnMobile}
                    onCut={preventCopyPasteOnMobile}
                    rows={currentQuestion.type === 'essay' ? 6 : 4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={
                      currentQuestion.type === 'essay'
                        ? t('Write your detailed answer here...')
                        : t('Type your answer here...')
                    }
                  />
                </div>
              )}

              {/* Number Questions */}
              {currentQuestion.type === 'Number' && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">{t('Your answer:')}</p>
                  <input
                    type="number"
                    value={answers[currentQuestion._id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                    onPaste={preventCopyPasteOnMobile}
                    onCopy={preventCopyPasteOnMobile}
                    onCut={preventCopyPasteOnMobile}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('Enter your answer...')}
                  />
                </div>
              )}

              {/* Code Questions */}
              {isCodeQuestion(currentQuestion) && (
                <div className="space-y-4">
                  <div
                    className={`rounded-xl border-2 px-4 py-3 sm:px-5 sm:py-4 ${
                      currentHiddenChecksRemaining <= 0
                        ? 'border-red-300 bg-red-50'
                        : currentHiddenChecksRemaining <= 2
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-indigo-200 bg-indigo-50'
                    }`}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3 min-w-0">
                        <BoltIcon
                          className={`h-7 w-7 shrink-0 mt-0.5 ${
                            currentHiddenChecksRemaining <= 0
                              ? 'text-red-600'
                              : currentHiddenChecksRemaining <= 2
                                ? 'text-amber-700'
                                : 'text-indigo-600'
                          }`}
                          aria-hidden
                        />
                        <div>
                          <p
                            className={`text-sm font-semibold sm:text-base ${
                              currentHiddenChecksRemaining <= 0 ? 'text-red-950' : 'text-gray-900'
                            }`}
                          >
                            Final check attempts left for this question
                          </p>
                          <p
                            className={`mt-1 text-sm leading-snug ${
                              currentHiddenChecksRemaining <= 0 ? 'text-red-900/90' : 'text-gray-800'
                            }`}
                          >
                            Run Code is unlimited. You have{' '}
                            <strong className="font-semibold">
                              {HIDDEN_CHECK_LIMIT_PER_QUESTION} Final Check attempts per question
                            </strong>{' '}
                            to validate against private tests. Save &amp; Next seals the answer for
                            this question.
                          </p>
                        </div>
                      </div>
                      <div
                        className={`flex shrink-0 items-center justify-center rounded-xl border-2 px-5 py-3 shadow-sm sm:min-w-[8.5rem] ${
                          currentHiddenChecksRemaining <= 0
                            ? 'border-red-200 bg-white'
                            : currentHiddenChecksRemaining <= 2
                              ? 'border-amber-200 bg-white'
                              : 'border-indigo-100 bg-white'
                        }`}
                      >
                        <div className="text-center">
                          <p
                            className={`text-3xl font-bold tabular-nums leading-none sm:text-4xl ${
                              currentHiddenChecksRemaining <= 0
                                ? 'text-red-600'
                                : currentHiddenChecksRemaining <= 2
                                  ? 'text-amber-800'
                                  : 'text-indigo-700'
                            }`}
                          >
                            {currentHiddenChecksRemaining}
                          </p>
                          <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                            of {HIDDEN_CHECK_LIMIT_PER_QUESTION} left
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                        Coding Assignment
                      </span>
                      {currentCodingMeta?.difficulty && (
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                          {currentCodingMeta.difficulty}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-600">Language</label>
                      <select
                        value={activeCodeLanguage}
                        onChange={(e) => {
                          const nextLanguage = e.target.value;
                          const previousLanguage = activeCodeLanguage;
                          const currentCode = getCodeAnswerString(answers[currentQuestion._id]);
                          const nextStarter = currentCodingMeta?.starterByLanguage?.[nextLanguage] || '';
                          const previousStarter =
                            currentCodingMeta?.starterByLanguage?.[previousLanguage] || '';
                          const shouldSwapToNewStarter =
                            !String(currentCode || '').trim() ||
                            String(currentCode || '').trim() === String(previousStarter || '').trim();
                          handleCodeAnswerChange(currentQuestion._id, {
                            language: nextLanguage,
                            code: shouldSwapToNewStarter ? nextStarter : currentCode,
                          });
                        }}
                        className="w-40 px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {allowedCodingLanguages.map((lang) => (
                          <option key={lang} value={lang}>
                            {getLanguageLabel(lang)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                      <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setActiveCodeTab('description')}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                            activeCodeTab === 'description'
                              ? 'bg-white border border-gray-300 text-gray-900'
                              : 'text-gray-600'
                          }`}
                        >
                          Description
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveCodeTab('test')}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                            activeCodeTab === 'test'
                              ? 'bg-white border border-gray-300 text-gray-900'
                              : 'text-gray-600'
                          }`}
                        >
                          Test Result
                        </button>
                      </div>
                      <div className="p-4 max-h-[26rem] overflow-y-auto">
                        {activeCodeTab === 'description' ? (
                          <>
                            <h4 className="text-base font-semibold text-gray-900 mb-2">
                              {currentQuestion.title || 'Problem Description'}
                            </h4>
                            <div className="space-y-3 text-sm text-gray-700">
                              <div>
                                <p className="font-semibold text-gray-900 mb-1">Problem Statement</p>
                                <p className="leading-6 whitespace-pre-wrap">
                                  {currentCodingMeta?.problemStatement ||
                                    currentQuestion.content ||
                                    'No problem statement available.'}
                                </p>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 mb-1">Input</p>
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap font-mono text-xs">
                                  {currentCodingMeta?.sampleInput || 'Input is not specified.'}
                                </pre>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 mb-1">Expected output</p>
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap font-mono text-xs">
                                  {currentCodingMeta?.sampleOutput || 'Expected output is not specified.'}
                                </pre>
                              </div>
                              {Array.isArray(currentCodingMeta?.evaluationLogic) &&
                                currentCodingMeta.evaluationLogic.length > 0 && (
                                  <div>
                                    <p className="font-semibold text-gray-900 mb-1">Evaluation Logic</p>
                                    <ul className="list-disc pl-5 space-y-1">
                                      {currentCodingMeta.evaluationLogic.map((point, idx) => (
                                        <li key={`${point}-${idx}`}>{point}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-700">
                            {!codeTestResult ? (
                              <div className="space-y-2 text-gray-500">
                                <p>Run Code while coding, then use Final Check attempts when ready.</p>
                                <p className="text-xs text-amber-800/90 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                                  Final Check attempts left for this question:{' '}
                                  <strong className="font-semibold text-amber-950">
                                    {currentHiddenChecksRemaining} of {HIDDEN_CHECK_LIMIT_PER_QUESTION}
                                  </strong>{' '}
                                  (Run Code does not consume this limit).
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                                      codeTestResult.mode === 'hidden'
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : 'bg-slate-100 text-slate-700'
                                    }`}
                                  >
                                    {codeTestResult.mode === 'hidden' ? 'Final Check' : 'Sample Run'}
                                  </span>
                                  {codeTestResult.mode === 'hidden' &&
                                    typeof codeTestResult.hiddenChecksRemaining === 'number' && (
                                      <span className="text-[11px] text-gray-500">
                                        {codeTestResult.hiddenChecksRemaining} attempt
                                        {codeTestResult.hiddenChecksRemaining === 1 ? '' : 's'} left
                                      </span>
                                    )}
                                </div>
                                <div
                                  className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                                    codeTestResult.correct
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : codeTestResult.implementationRejected
                                        ? 'bg-amber-100 text-amber-900'
                                        : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {codeTestResult.correct
                                    ? 'Correct'
                                    : codeTestResult.implementationRejected
                                      ? 'Output only'
                                      : 'Incorrect'}
                                </div>
                                <p>{codeTestResult.message || 'Result received.'}</p>
                                {codeTestResult.syntaxError && (
                                  <div className="text-xs text-red-600 space-y-1">
                                    <p>Please fix syntax errors and test again.</p>
                                    {typeof codeTestResult.syntaxErrorLine === 'number' && (
                                      <p>
                                        Error at line {codeTestResult.syntaxErrorLine}
                                        {typeof codeTestResult.syntaxErrorColumn === 'number'
                                          ? `, column ${codeTestResult.syntaxErrorColumn}`
                                          : ''}
                                        .
                                      </p>
                                    )}
                                  </div>
                                )}
                                {codeTestResult.runtimeError && (
                                  <div className="text-xs text-red-600 space-y-1">
                                    <p>Fix the runtime error and try again.</p>
                                    {typeof codeTestResult.runtimeErrorLine === 'number' && (
                                      <p>
                                        Error at line {codeTestResult.runtimeErrorLine}
                                        {typeof codeTestResult.runtimeErrorColumn === 'number'
                                          ? `, column ${codeTestResult.runtimeErrorColumn}`
                                          : ''}
                                        .
                                      </p>
                                    )}
                                  </div>
                                )}
                                {codeTestResult.validatorUnavailable && (
                                  <p className="text-xs text-amber-600">
                                    Syntax validator is currently unavailable for this language.
                                  </p>
                                )}
                                {codeTestResult.missingExpectedSolution && (
                                  <p className="text-xs text-amber-600">
                                    Expected solution is missing for this template/language.
                                  </p>
                                )}
                                {typeof (codeTestResult.matchScore ?? codeTestResult.similarity) === 'number' && (
                                  <p className="text-xs text-gray-500">
                                    Match score:{' '}
                                    {Math.round(
                                      (codeTestResult.matchScore ?? codeTestResult.similarity) * 100
                                    )}
                                    %
                                  </p>
                                )}
                                {codeTestResult.outputMatched &&
                                  typeof codeTestResult.similarity === 'number' &&
                                  Math.round(codeTestResult.similarity * 100) !==
                                    Math.round(
                                      (codeTestResult.matchScore ?? codeTestResult.similarity) * 100
                                    ) && (
                                    <p className="text-[11px] text-gray-400">
                                      Code similarity: {Math.round(codeTestResult.similarity * 100)}%
                                    </p>
                                  )}
                                {(codeTestResult.output || codeTestResult.expectedOutput) && (
                                  <div className="space-y-2">
                                    <div>
                                      <p className="text-xs font-medium text-gray-700">Output</p>
                                      <pre className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs whitespace-pre-wrap">
                                        {codeTestResult.output || 'No output'}
                                      </pre>
                                    </div>
                                    <div>
                                      <p className="text-xs font-medium text-gray-700">Expected Output</p>
                                      <pre className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs whitespace-pre-wrap">
                                        {codeTestResult.expectedOutput || 'Not configured'}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-900 bg-[#111827] overflow-hidden">
                      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-b border-gray-700 bg-[#1f2937]">
                        <span className="text-xs font-medium text-gray-200">Code</span>
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                          <div
                            className={`order-last sm:order-none rounded-lg px-2.5 py-1.5 text-center shadow-inner sm:min-w-[5.5rem] ${
                              currentHiddenChecksRemaining <= 0
                                ? 'bg-red-950/80 ring-1 ring-red-500/60'
                                : currentHiddenChecksRemaining <= 2
                                  ? 'bg-amber-950/50 ring-1 ring-amber-500/50'
                                  : 'bg-gray-950/50 ring-1 ring-indigo-500/40'
                            }`}
                            title={`${currentHiddenChecksRemaining} of ${HIDDEN_CHECK_LIMIT_PER_QUESTION} Final Check attempts left for this question`}
                          >
                            <p
                              className={`text-lg font-bold tabular-nums leading-none ${
                                currentHiddenChecksRemaining <= 0
                                  ? 'text-red-200'
                                  : currentHiddenChecksRemaining <= 2
                                    ? 'text-amber-100'
                                    : 'text-indigo-100'
                              }`}
                            >
                              {currentHiddenChecksRemaining}
                            </p>
                            <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                              / {HIDDEN_CHECK_LIMIT_PER_QUESTION} attempts
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleFormatCode}
                            className="text-[11px] px-2 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
                          >
                            Format
                          </button>
                          <button
                            type="button"
                            onClick={() => runCodeCheck('sample')}
                            disabled={isTestingCode || isSubmitting}
                            className="text-[11px] px-2 py-1 rounded bg-slate-600 text-white hover:bg-slate-500 disabled:opacity-60"
                          >
                            {isTestingCode ? 'Running...' : 'Run Code'}
                          </button>
                          <button
                            type="button"
                            onClick={() => runCodeCheck('hidden')}
                            disabled={isTestingCode || currentHiddenChecksRemaining <= 0 || isSubmitting}
                            className="text-[11px] px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
                          >
                            {isTestingCode ? 'Checking...' : 'Final Check'}
                          </button>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {getLanguageLabel(activeCodeLanguage)}
                          </span>
                        </div>
                      </div>
                      <CodeMirror
                        value={getCodeAnswerString(answers[currentQuestion._id])}
                        height="360px"
                        theme={oneDark}
                        basicSetup={{
                          lineNumbers: true,
                          foldGutter: true,
                          highlightActiveLine: true,
                          autocompletion: true,
                        }}
                        extensions={getEditorExtensions(activeCodeLanguage)}
                        onChange={(value) =>
                          handleCodeAnswerChange(currentQuestion._id, { code: value })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Default text input for any other question types */}
              {![
                'multiple-choice',
                'Multiple Choice (Single)',
                'Multiple Choice (Multiple)',
                'multiple-choice-multiple',
                'single-choice',
                'true-false',
                'True/False',
                'text',
                'essay',
                'short-answer',
                'Short Answer / Fill in the Blank',
                'Number',
                'Code',
                'code',
                'passage-reading',
                'paragraph-reading',
                QUESTION_TYPES.FILL_IN_BLANKS,
                'Write the basic accounting equation',
              ].includes(currentQuestion.type) &&
                !(currentQuestion.type && currentQuestion.type.includes('Multiple Choice')) &&
                !isPassageReadingQuestion(currentQuestion) && (
                  <div>
                    {(() => { console.warn('[AssessmentTake] Question fell to default text input. type:', currentQuestion.type, 'hasOptions:', !!(currentQuestion.options && currentQuestion.options.length > 0), 'qId:', currentQuestion._id, 'title:', (currentQuestion.title || '').substring(0, 60)); return null; })()}
                    <p className="text-sm font-medium text-gray-700 mb-3">{t('Your answer:')}</p>
                    <textarea
                      value={answers[currentQuestion._id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                      onPaste={preventCopyPasteOnMobile}
                      onCopy={preventCopyPasteOnMobile}
                      onCut={preventCopyPasteOnMobile}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('Type your answer here...')}
                    />
                  </div>
                )}
            </div>

            {/* Navigation – compact on mobile */}
            <div className="flex items-center justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 pb-36 sm:pb-0 border-t border-gray-200 gap-2">
              <button
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
                className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeftIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                {t('Previous')}
              </button>

              <div className="flex items-center" />

              {currentQuestionIndex === allQuestions.length - 1 ? (
                <button
                  onClick={sealCurrentQuestionAndContinue}
                  data-testid="next-question-btn"
                  disabled={isSubmitting}
                  className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? t('Loading...') : t('Review Answers')}
                </button>
              ) : (
                <button
                  onClick={sealCurrentQuestionAndContinue}
                  data-testid="next-question-btn"
                  className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('Save & Next')}
                  <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-gray-600">{t('No questions available.')}</p>
          </div>
        )}
      </div>

      {/* Floating button to open Question Navigator when closed */}
      {!isNavigatorOpen && !isPersonalityQuestionSet && (
        <button
          type="button"
          onClick={() => setIsNavigatorOpen(true)}
          className="fixed right-3 top-20 z-30 flex items-center gap-2 bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:right-4"
          aria-label={t('Open question navigator')}
        >
          <ListBulletIcon className="h-5 w-5 text-indigo-600" />
          <span className="hidden sm:inline">{t('Questions')}</span>
          <span className="text-xs text-gray-600 sm:hidden">{currentQuestionIndex + 1}/{allQuestions.length}</span>
        </button>
      )}

      {/* Question Navigator – toggleable panel; compact on mobile */}
      {isNavigatorOpen && !isPersonalityQuestionSet && (
        <div className="fixed right-2 top-16 left-2 sm:left-auto sm:w-64 z-40 bg-white rounded-xl shadow-xl border border-gray-200 p-3 sm:p-4 max-h-[85vh] overflow-y-auto sm:right-4 sm:top-20">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-xs font-semibold text-gray-900 sm:text-sm">{t('Question Navigator')}</h3>
            <button
              type="button"
              onClick={() => setIsNavigatorOpen(false)}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label={t('Close question navigator')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-5 gap-1.5 sm:gap-2">
            {allQuestions.map((question, index) => (
              <button
                key={question._id}
                onClick={() => goToQuestion(index)}
                className={`w-7 h-7 sm:w-8 sm:h-8 text-xs rounded-md border transition-colors flex items-center justify-center ${
                  index === currentQuestionIndex
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : isNavigatorAnswered(question)
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : isNavigatorDraft(question)
                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <div className="mt-3 sm:mt-4 text-[11px] sm:text-xs text-gray-500 space-y-1">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-indigo-600 rounded flex-shrink-0" />
              <span>{t('Current')}</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-100 border border-green-300 rounded flex-shrink-0" />
              <span>{t('Sealed')}</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-100 border border-amber-300 rounded flex-shrink-0" />
              <span>{t('Draft')}</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-gray-100 border border-gray-300 rounded flex-shrink-0" />
              <span>{t('Unanswered')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Time Warning Modal – compact on mobile */}
      {showTimeWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 flex-shrink-0" />
              <h3 className="text-base font-medium text-gray-900 sm:text-lg">{t('Time Warning')}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4 sm:text-base">
              {t('You have 5 minutes remaining to complete your assessment. Please review your answers and submit when ready.')}
            </p>
            <button
              onClick={() => setShowTimeWarning(false)}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
            >
              {t('Got it')}
            </button>
          </div>
        </div>
      )}

      {/* Review before final submit */}
      {showReviewScreen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 pb-36 sm:p-6 sm:pb-6 max-w-3xl w-full shadow-xl max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-gray-900 mb-1 sm:text-lg">{t('Review Answers')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('Confirm each question before final submit. You can still jump back and edit.')}
            </p>
            <div className="space-y-2">
              {allQuestions.map((q, idx) => {
                const p = getQuestionProgress(q._id);
                const hasValue = Boolean(answers[q._id]);
                const sealed = p.status === 'sealed';
                const statusLabel = sealed ? 'Sealed' : hasValue ? 'Draft' : 'Skipped';
                const statusClass = sealed
                  ? 'bg-green-100 text-green-800 border-green-300'
                  : hasValue
                    ? 'bg-amber-100 text-amber-800 border-amber-300'
                    : 'bg-gray-100 text-gray-600 border-gray-300';
                const hiddenUsed = Number(p.hiddenChecksUsed || 0);
                return (
                  <div
                    key={q._id}
                    className="border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Q{idx + 1}. {q.title || t('Untitled question')}
                      </p>
                      {isCodeQuestion(q) && (
                        <div className="text-[11px] text-gray-500 space-y-0.5">
                          <p>Hidden checks used: {hiddenUsed}/{HIDDEN_CHECK_LIMIT_PER_QUESTION}</p>
                          <p>
                            Final check:{' '}
                            {p.lastFinalCheckPassed === true
                              ? 'Passed'
                              : p.lastFinalCheckPassed === false
                                ? 'Not passed yet'
                                : 'Not run yet'}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] px-2 py-1 rounded border ${statusClass}`}>
                        {t(statusLabel)}
                      </span>
                      {sealed && p.lastFinalCheckPassed === true && (
                        <span
                          className="text-[10px] text-emerald-700"
                          title="Final Check passed for this question."
                        >
                          Verified by Final Check
                        </span>
                      )}
                      {sealed && p.lastFinalCheckPassed !== true && (
                        <span
                          className="text-[10px] text-gray-500"
                          title={t('Sealed means saved for submission. It does not mean correct.')}
                        >
                          {t('Saved (not graded)')}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setShowReviewScreen(false);
                          goToQuestion(idx);
                        }}
                        className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        {t('Edit')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 sm:gap-3 mt-5">
              <button
                onClick={() => setShowReviewScreen(false)}
                className="flex-1 px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('Back')}
              </button>
              <button
                onClick={() => {
                  setShowReviewScreen(false);
                  setShowConfirmSubmit(true);
                }}
                data-testid="continue-to-submit-btn"
                className="flex-1 px-3 py-2 sm:px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                {assessment.isMultiModule && !assessment.isRubricBased && currentModuleIndex < (assessment.modules?.length || 1) - 1
                  ? t('Continue')
                  : t('Continue to Submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Submit Modal – compact on mobile */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full shadow-xl">
            {(() => {
              const isIntermediateModule =
                assessment.isMultiModule && !assessment.isRubricBased &&
                currentModuleIndex < (assessment.modules?.length || 1) - 1;
              return (
                <>
                  <h3 className="text-base font-medium text-gray-900 mb-3 sm:mb-4 sm:text-lg">
                    {isIntermediateModule ? t('Complete this module?') : t('Submit Assessment?')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 sm:text-base">
                    {isIntermediateModule
                      ? t('Are you sure you want to complete this module? You won’t be able to return to it, and the next module will begin.')
                      : t('Are you sure you want to submit your assessment? This action cannot be undone.')}
                  </p>
                </>
              );
            })()}

            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleSubmitAssessment}
                data-testid="confirm-submit-btn"
                disabled={isSubmitting}
                className="flex-1 px-3 py-2 sm:px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSubmitting
                  ? t('Submitting...')
                  : assessment.isMultiModule && !assessment.isRubricBased && currentModuleIndex < (assessment.modules?.length || 1) - 1
                    ? t('Save & Next Module')
                    : t('Submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentTake;
